import * as THREE from "three";
import type { AttachKind } from "@rietz/shared";
import {
  WEAPON_BARREL_COLOR,
  WEAPON_LENS_IDLE_COLOR,
  WEAPON_RECEIVER_COLOR,
  WEAPON_SIGHT_BEAD_COLOR,
  WEAPON_SIGHT_COLOR,
  WEAPON_STOCK_COLOR,
} from "./palette.js";

/**
 * The weapon models — one per gun in the item DB, assembled from boxes in the
 * visual language of `character.ts` (shared geometry, flat Lambert, palette
 * colours). Drawn in the player's own hands (`viewmodel.ts`) and on every
 * remote player (`character.ts`). Each silhouette is built around overall
 * length and ONE distinctive lump (carbine: wood + irons; smg: short, stick mag;
 * shotgun: tube under barrel + pump; dmr: long + scope; pistol: a third the
 * length; east_carbine: curved mag + gas tube; sniper: longest, fat glass).
 *
 * LOCAL SPACE CONTRACT: the gun points down −Z; the origin is on the SIGHT
 * AXIS (irons or scope optical axis), so centring the group in front of the
 * camera is aiming down the sights; `muzzle` is a child at the barrel tip;
 * `grips` says where the two hands close.
 *
 * ATTACHMENTS. SWAP kinds (stock, mag, barrel, handguard, pistolgrip, receiver)
 * are COMPONENTS — named `component:<kind>` groups — and what is fitted decides
 * which component exists (see `SwapSlot`). ADD kinds (optic, muzzle, grip,
 * tactical) are lumps at `AttachAnchors` positions (`applyMods`). An optic owns
 * the sight axis and hides the irons; a magnifying one hands out its ocular as
 * a `ScopeSurface` for the viewmodel's picture-in-picture. A muzzle device
 * moves `muzzle` to its own tip, continuing from the fitted barrel's end.
 *
 * Only the own viewmodel and the mod screen get mods — `EntitySnap.gear` is a
 * def id, so remote avatars and dropped meshes keep the base silhouette.
 * `buildPart` draws a part alone for its inventory tile.
 *
 * EMPTY SWAP SLOTS (`BuildWeaponOptions.bare`): `"missing"` — the caller holds
 * the real parts list, so an empty slot draws nothing (a stripped rifle looks
 * stripped, an empty well looks empty); `"factory"` (default) — the caller was
 * never told (remote player, dropped item), so the gun wears its factory parts.
 */

/**
 * The ocular lens of a magnifying scope: the disc the viewmodel paints its
 * picture-in-picture onto. The mesh takes the live texture; the radius sets
 * the magnified fov (`scopeFovFor`).
 */
export interface ScopeSurface {
  lens: THREE.Mesh;
  radiusM: number;
}

/**
 * Where the two hands sit on a gun, in local space. Not part of `modAnchors`,
 * which the workbench walks wholesale to draw a callout per slot.
 */
export interface HandGrips {
  /** Trigger hand: the pistol grip, or the wrist of a stock that has none. */
  trigger: THREE.Vector3;
  /** Support hand: the handguard, the fore-end, the pump. */
  support: THREE.Vector3;
}

export interface BuiltWeapon {
  group: THREE.Group;
  /** Empty object at the barrel tip — parent muzzle flashes to this. */
  muzzle: THREE.Object3D;
  /** Overall length along −Z, metres. Purely informational. */
  lengthM: number;
  /** Local-space mounting points used by the workbench's live callout lines. */
  modAnchors: Readonly<Partial<Record<AttachKind, THREE.Vector3>>>;
  /** Where the hands go. Fresh vectors per build, like `modAnchors`. */
  grips: Readonly<HandGrips>;
  /**
   * The detachable magazine currently in the gun — the whole component, since
   * the reload animation drops it. Absent on the shotgun (tube), the pistol
   * (box inside the grip) and any gun with an empty well in `"missing"` view.
   * Belongs to the group; never dispose it separately.
   */
  magMesh?: THREE.Object3D;
  /** Present only on the guns wearing glass that magnifies. See `ScopeSurface`. */
  scope?: ScopeSurface;
  dispose(): void;
}

/**
 * Shared across every weapon ever built — two guns in a scene must not be two
 * sets of buffers.
 */
const geoCache = new Map<string, THREE.BoxGeometry>();

function boxGeo(w: number, h: number, d: number): THREE.BoxGeometry {
  const key = `${w}|${h}|${d}`;
  let g = geoCache.get(key);
  if (g === undefined) {
    g = new THREE.BoxGeometry(w, h, d);
    geoCache.set(key, g);
  }
  return g;
}

/**
 * THE aiming mark, shared by every sight: a spike rising to a needle tip, and
 * the TIP is the point of impact. It hangs below its tip so it never covers the
 * target. Optics draw it as a texture; irons get the same shape as geometry.
 */
const RETICLE_QUAD_M = 0.005;
/** Spike height/base half-width as fractions of the quad. Tip at quad centre. */
const RETICLE_SPIKE_H = 0.42;
const RETICLE_SPIKE_HALF_W = 0.07;

/** Shared like `geoCache`: one quad, one spike texture, however many guns. */
let reticleGeo: THREE.PlaneGeometry | null = null;
let reticleTex: THREE.Texture | null = null;

function reticleGeometry(): THREE.PlaneGeometry {
  if (reticleGeo === null) reticleGeo = new THREE.PlaneGeometry(RETICLE_QUAD_M, RETICLE_QUAD_M);
  return reticleGeo;
}

function reticleTexture(): THREE.Texture {
  if (reticleTex !== null) return reticleTex;
  // Headless (smoke scripts): no canvas 2D, so a single bright texel.
  if (typeof document === "undefined") {
    const r = (WEAPON_SIGHT_BEAD_COLOR >> 16) & 0xff;
    const g = (WEAPON_SIGHT_BEAD_COLOR >> 8) & 0xff;
    const b = WEAPON_SIGHT_BEAD_COLOR & 0xff;
    reticleTex = new THREE.DataTexture(new Uint8Array([r, g, b, 255]), 1, 1);
    reticleTex.needsUpdate = true;
    return reticleTex;
  }
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx !== null) {
    // Same colour as the fiber tips on the iron sights, so every aiming mark
    // in the game is the same light.
    const r = (WEAPON_SIGHT_BEAD_COLOR >> 16) & 0xff;
    const g = (WEAPON_SIGHT_BEAD_COLOR >> 8) & 0xff;
    const b = WEAPON_SIGHT_BEAD_COLOR & 0xff;
    const half = size / 2;
    // Tip exactly at the quad centre (= the sight axis). Hard edges.
    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.beginPath();
    ctx.moveTo(half, half);
    ctx.lineTo(half - size * RETICLE_SPIKE_HALF_W, half + size * RETICLE_SPIKE_H);
    ctx.lineTo(half + size * RETICLE_SPIKE_HALF_W, half + size * RETICLE_SPIKE_H);
    ctx.closePath();
    ctx.fill();
  }
  reticleTex = new THREE.CanvasTexture(canvas);
  return reticleTex;
}

/** One transparent quad with the spike texture, facing +Z (the aiming eye). Depth
 *  writes off so the corners never punch holes. Material is per-build. */
function reticleQuad(): { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial } {
  const mat = new THREE.MeshBasicMaterial({
    map: reticleTexture(),
    transparent: true,
    depthWrite: false,
  });
  return { mesh: new THREE.Mesh(reticleGeometry(), mat), mat };
}

/**
 * The ocular glass of a magnifying scope: a disc on the sight axis facing +Z.
 * Real geometry because the viewmodel paints a second world render onto it.
 * Not in `geoCache` (every scope has its own radius); `finish` disposes it.
 * 40 segments — fewer and the picture's edge is visibly a polygon.
 */
function lensDisc(radiusM: number): { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial } {
  const mat = new THREE.MeshBasicMaterial({ color: WEAPON_LENS_IDLE_COLOR });
  return { mesh: new THREE.Mesh(new THREE.CircleGeometry(radiusM, 40), mat), mat };
}

/**
 * The material set a gun is painted from — one per built weapon, since rigs
 * dispose their own models. Geometries are shared.
 */
interface WeaponMaterials {
  stock: THREE.MeshLambertMaterial;
  receiver: THREE.MeshLambertMaterial;
  barrel: THREE.MeshLambertMaterial;
  sight: THREE.MeshLambertMaterial;
  bead: THREE.MeshBasicMaterial;
  all: { dispose(): void }[];
}

function makeMaterials(): WeaponMaterials {
  const stock = new THREE.MeshLambertMaterial({
    color: WEAPON_STOCK_COLOR,
    flatShading: true,
  });
  const receiver = new THREE.MeshLambertMaterial({
    color: WEAPON_RECEIVER_COLOR,
    flatShading: true,
  });
  const barrel = new THREE.MeshLambertMaterial({
    color: WEAPON_BARREL_COLOR,
    flatShading: true,
  });
  const sight = new THREE.MeshLambertMaterial({
    color: WEAPON_SIGHT_COLOR,
    flatShading: true,
  });
  // Unlit on purpose — see the palette note on the bead colour.
  const bead = new THREE.MeshBasicMaterial({ color: WEAPON_SIGHT_BEAD_COLOR });
  return { stock, receiver, barrel, sight, bead, all: [stock, receiver, barrel, sight, bead] };
}

// ---------------------------------------------------------------------------
// Components: the swappable regions of a gun
// ---------------------------------------------------------------------------

/** How an empty SWAP slot is drawn. See the note at the top of the file. */
export type BareSlotView = "factory" | "missing";

export interface BuildWeaponOptions {
  /** What an empty swap slot means to this caller. Defaults to `"factory"`. */
  bare?: BareSlotView;
}

/**
 * The kinds whose region of the gun is a component. Build/mount order; `barrel`
 * first because everything running forward stops short of the muzzle
 * (`ComponentCtx.muzzleZ`, `spanToMuzzle`).
 */
const SWAP_ORDER = [
  "barrel",
  "receiver",
  "handguard",
  "stock",
  "pistolgrip",
  "mag",
] as const;

type SwapKind = (typeof SWAP_ORDER)[number];

/**
 * What a component builder may know about the rest of the gun — two facts, so a
 * stock cannot reach into the magazine.
 */
interface ComponentCtx {
  /** Whether this build is drawing iron sights (an optic suppresses them). */
  irons: boolean;
  /** The CURRENT barrel tip. Valid for every component built after the barrel. */
  muzzleZ: number;
}

/**
 * One built region of a gun plus what it alone knows about the anchors: a
 * barrel knows the muzzle, a magazine knows how far down it hangs. Every field
 * is optional and overrides the gun's `AttachAnchors` entry (see `absorb`).
 */
interface WeaponComponent {
  group: THREE.Group;
  /** Barrel components: the bare tip a muzzle device continues from. */
  muzzleZ?: number;
  /** Magazine components: the bottom of the box, where an extension would grow. */
  magY?: number;
  magZ?: number;
  /** Tube-magazine components (the shotgun): the tip of the tube. */
  tubeY?: number;
  tubeZ?: number;
  /** Stock components: centre height and rear face of the butt. */
  buttY?: number;
  buttZ?: number;
  /** Magazine components that DROP during a reload. See `BuiltWeapon.magMesh`. */
  magMesh?: THREE.Object3D;
}

type ComponentBuilder = (mats: WeaponMaterials, ctx: ComponentCtx) => WeaponComponent;

/**
 * One swappable region — "what is fitted decides what exists".
 *
 * `factoryKind` mirrors `WeaponSlotDef.factoryDefId`: `"integral"` = the slot
 * ships with no separate part, so the factory component is drawn when empty in
 * BOTH views (no slot in the roster is integral today; the shotgun's pump is
 * base geometry instead); `"detachable"` = a real part that can come off, drawn
 * as the factory part in `"factory"` view and as nothing in `"missing"` view.
 * Magazines use the older convention — the first true magazine in `accepts` is
 * the factory box (`factoryMagFor`).
 */
interface SwapSlot {
  /** What the gun wears when nothing loose is fitted here. */
  factory: ComponentBuilder;
  factoryKind: "integral" | "detachable";
  /**
   * Components for the loose parts this slot accepts, by def id. An unknown id
   * draws the factory component rather than nothing.
   */
  parts?: Readonly<Record<string, ComponentBuilder>>;
  /**
   * For barrel slots that can be empty: where the muzzle anchor sits with no
   * barrel.
   */
  emptyMuzzleZ?: number;
}

type WeaponSwaps = Partial<Record<SwapKind, SwapSlot>>;

/** Positions a mesh inside a component and returns it. The component-local
 *  idiom of the `add` closure `applyMods` uses, so both read the same. */
function put(g: THREE.Group, mesh: THREE.Mesh, x: number, y: number, z: number): THREE.Mesh {
  mesh.position.set(x, y, z);
  g.add(mesh);
  return mesh;
}

/**
 * The front sight, which lives on the barrel and travels with it (a stub barrel
 * would otherwise leave the post floating). The rear notch stays on the
 * receiver. The blade's top touches the sight axis from underneath.
 */
function frontSight(
  g: THREE.Group,
  mats: WeaponMaterials,
  bladeH: number,
  z: number,
): void {
  put(g, new THREE.Mesh(boxGeo(0.004, bladeH, 0.008), mats.sight), 0, -bladeH / 2 - 0.002, z);
}

/**
 * A fore-end, shroud or magazine tube stopped short of the muzzle: `zRear` is
 * fixed at the receiver, `zWanted` is the full-length reach, and whichever is
 * nearer the shooter wins — so a coach barrel pulls the tube back with it.
 */
function spanToMuzzle(
  zRear: number,
  zWanted: number,
  muzzleZ: number,
  gap: number,
  minDepth = 0.04,
): { center: number; depth: number } {
  const front = Math.min(zRear - minDepth, Math.max(zWanted, muzzleZ + gap));
  return { center: (zRear + front) / 2, depth: zRear - front };
}

/**
 * Picks the component for one slot: fitted part, else factory, else nothing.
 * Looked up in the slot's own table rather than via `getItemDef`, so this file
 * cannot draw a part it does not know and has no item-DB dependency.
 */
function buildSwap(
  slot: SwapSlot,
  mods: readonly string[],
  bare: BareSlotView,
  mats: WeaponMaterials,
  ctx: ComponentCtx,
): WeaponComponent | null {
  const parts = slot.parts;
  if (parts !== undefined) {
    for (const id of mods) {
      const make = parts[id];
      if (make !== undefined) return make(mats, ctx);
    }
  }
  if (slot.factoryKind === "integral") return slot.factory(mats, ctx);
  return bare === "factory" ? slot.factory(mats, ctx) : null;
}

/** Folds a component's own measurements back into the gun's anchor table. */
function absorb(a: AttachAnchors, c: WeaponComponent): void {
  if (c.muzzleZ !== undefined) a.muzzleZ = c.muzzleZ;
  if (c.magY !== undefined) a.magY = c.magY;
  if (c.magZ !== undefined) a.magZ = c.magZ;
  if (c.tubeY !== undefined) a.tubeY = c.tubeY;
  if (c.tubeZ !== undefined) a.tubeZ = c.tubeZ;
  if (c.buttY !== undefined) a.buttY = c.buttY;
  if (c.buttZ !== undefined) a.buttZ = c.buttZ;
}

/**
 * Builds every swap slot, mounts the result and updates `a` — the build's OWN
 * copy of the anchors, never the shared `*_ANCHORS` constant.
 */
function mountSwaps(
  group: THREE.Group,
  mats: WeaponMaterials,
  swaps: WeaponSwaps,
  mods: readonly string[],
  bare: BareSlotView,
  a: AttachAnchors,
  irons: boolean,
): { magMesh?: THREE.Object3D } {
  const ctx: ComponentCtx = { irons, muzzleZ: a.muzzleZ };
  let magMesh: THREE.Object3D | undefined;

  for (const kind of SWAP_ORDER) {
    const slot = swaps[kind];
    if (slot === undefined) continue;

    const built = buildSwap(slot, mods, bare, mats, ctx);
    if (built !== null) {
      built.group.name = `component:${kind}`;
      group.add(built.group);
      absorb(a, built);
      if (built.magMesh !== undefined) magMesh = built.magMesh;
    } else if (kind === "barrel" && slot.emptyMuzzleZ !== undefined) {
      // No barrel at all: the flash comes out of the trunnion, because that is
      // where the hole in the rifle is.
      a.muzzleZ = slot.emptyMuzzleZ;
    }
    if (kind === "barrel") {
      ctx.muzzleZ = a.muzzleZ;
      // A light cannot be clamped past the end of the gun: the MP 9's stub
      // barrel pulls the muzzle behind the factory `tacticalZ`.
      if (a.tacticalZ !== undefined) a.tacticalZ = Math.max(a.tacticalZ, a.muzzleZ + 0.045);
    }
  }

  return { magMesh };
}

/**
 * What a builder discovered about its own model that the anchor table cannot
 * say.
 */
interface BuiltParts {
  /** Where the hands go. Required: every gun is held. See `GripPoints`. */
  grips: GripPoints;
  /** The magazine component, when one is in the gun. See `BuiltWeapon`. */
  magMesh?: THREE.Object3D;
  /** Magnifying glass, when the build ended up with some. */
  scope?: ScopeSurface;
}

function finish(
  group: THREE.Group,
  mats: WeaponMaterials,
  muzzle: THREE.Object3D,
  lengthM: number,
  anchors: AttachAnchors,
  parts: BuiltParts,
): BuiltWeapon {
  const scope = parts.scope;
  return {
    group,
    muzzle,
    lengthM,
    scope,
    magMesh: parts.magMesh,
    grips: {
      trigger: new THREE.Vector3(0, parts.grips.triggerY, parts.grips.triggerZ),
      support: new THREE.Vector3(0, parts.grips.supportY, parts.grips.supportZ),
    },
    modAnchors: {
      optic: new THREE.Vector3(0, 0, anchors.opticZ),
      muzzle: new THREE.Vector3(0, anchors.barrelY, anchors.muzzleZ),
      grip: new THREE.Vector3(0, anchors.forendY, anchors.forendZ),
      stock: new THREE.Vector3(0, anchors.buttY, anchors.buttZ),
      mag: new THREE.Vector3(
        0,
        anchors.tubeY ?? anchors.magY,
        anchors.tubeZ ?? anchors.magZ,
      ),
      // The v18 five. Each falls back to the nearest honest point on the gun's
      // own geometry rather than to zero — see the note on `AttachAnchors`.
      receiver: new THREE.Vector3(
        0,
        anchors.receiverY ?? anchors.barrelY + 0.02,
        anchors.receiverZ ?? anchors.opticZ,
      ),
      barrel: new THREE.Vector3(
        0,
        anchors.barrelY,
        anchors.barrelZ ?? (anchors.muzzleZ + anchors.forendZ) / 2,
      ),
      handguard: new THREE.Vector3(
        0,
        anchors.handguardY ?? anchors.forendY + 0.02,
        anchors.handguardZ ?? anchors.forendZ,
      ),
      pistolgrip: new THREE.Vector3(
        0,
        anchors.pistolgripY ?? anchors.forendY - 0.04,
        anchors.pistolgripZ ?? 0.06,
      ),
      tactical: new THREE.Vector3(
        0,
        anchors.tacticalY ?? anchors.forendY,
        anchors.tacticalZ ?? anchors.forendZ - 0.06,
      ),
    },
    dispose(): void {
      // Geometries are shared and long-lived (see `geoCache`); only the
      // materials belong to this instance — same bargain as `character.ts`.
      for (const m of mats.all) m.dispose();
      // The one exception: a lens disc is sized to its own scope, so its
      // CircleGeometry was built here and nothing else can ever want it.
      scope?.lens.geometry.dispose();
      group.clear();
    },
  };
}

/** Places the muzzle anchor and adds it to the group. */
function addMuzzle(group: THREE.Group, y: number, z: number): THREE.Object3D {
  const muzzle = new THREE.Object3D();
  muzzle.name = "muzzle";
  muzzle.position.set(0, y, z);
  group.add(muzzle);
  return muzzle;
}

/**
 * Where each kind of part bolts onto a given gun, in local space (`y` from the
 * sight axis, `z` negative toward the muzzle). One table per silhouette so
 * parts are authored once. A STARTING POINT: each builder copies it and the
 * mounted components overwrite what they alone know (see `absorb`). An empty
 * slot keeps the constant, so a leader line still points at the well.
 */
interface AttachAnchors {
  /** Barrel axis height and the tip the muzzle device continues from. */
  barrelY: number;
  muzzleZ: number;
  /** Underside of the fore-end, where a vertical grip hangs. */
  forendY: number;
  forendZ: number;
  /** Bottom of the magazine, where an extension or a drum grows down. */
  magY: number;
  magZ: number;
  /** Centre height and REAR FACE of the butt, where a pad is added. */
  buttY: number;
  buttZ: number;
  /** Centre of the optic mount, over the receiver. Glass rides at y = 0. */
  opticZ: number;
  /** Tip of an under-barrel magazine tube (shotgun). Absent on box-fed guns. */
  tubeY?: number;
  tubeZ?: number;

  // --- Optional kinds; each falls back to a point derived from the five above
  // (see `finish`), because the workbench walks `modAnchors` wholesale and a
  // zero would be a leader line pointing at the sight axis.

  /** Top face of the receiver, where a dust or top cover lies. */
  receiverY?: number;
  receiverZ?: number;
  /** Centre of the barrel run, where a swapped tube is drawn. */
  barrelZ?: number;
  /** Centre of the fore-end shell a handguard replaces. */
  handguardY?: number;
  handguardZ?: number;
  /** Centre of the FIRING hand's grip — a different thing from `forend`. */
  pistolgripY?: number;
  pistolgripZ?: number;
  /** Under the fore-end, forward of any vertical grip: lights and lasers. */
  tacticalY?: number;
  tacticalZ?: number;
}

/**
 * Where the hands go on one silhouette, on the centreline (`x` is always 0).
 * Vectors are built fresh per `finish` — the reload animation moves one of
 * these every frame. The trigger point is the centre of the fist.
 */
interface GripPoints {
  triggerY: number;
  triggerZ: number;
  supportY: number;
  supportZ: number;
}

/**
 * Builds a weapon by def id, optionally with fitted attachment def ids.
 *
 * `mods` is a flat, deep list in fold order — `fittedModsDeep(def, item).map(f
 * => f.def.id)`; depth does not matter to the model. `opts.bare` says what an
 * empty swap slot means (default `"factory"`). Unknown weapon ids fall back to
 * the carbine and unknown mod ids are skipped: a wire id must never take the
 * renderer down.
 */
export function buildWeapon(
  id: string,
  mods: readonly string[] = [],
  opts: BuildWeaponOptions = {},
): BuiltWeapon {
  const bare = opts.bare ?? "factory";
  switch (id) {
    case "carbine":
      return buildCarbine(mods, bare);
    case "smg":
      return buildSmg(mods, bare);
    case "shotgun":
      return buildShotgun(mods, bare);
    case "dmr":
      return buildDmr(mods, bare);
    case "pistol":
      return buildPistol(mods, bare);
    case "east_carbine":
      return buildEastCarbine(mods, bare);
    case "sniper":
      return buildSniper(mods, bare);
    default:
      console.warn(`buildWeapon: unknown weapon "${id}"; drawing the carbine`);
      return buildCarbine(mods, bare);
  }
}

/**
 * The optic def ids this file can draw; their presence hides the irons. A
 * hardcoded list rather than an `attachKind` test so an optic the builder does
 * not know yet keeps the irons instead of leaving no sight at all. The two
 * factory scopes (`factory_scope_4x`, `sniper_scope_10x`) are absent on purpose:
 * their own gun's scope block draws them (see `buildDmr`).
 */
const OPTIC_IDS: readonly string[] = [
  "red_dot",
  "scope_2x",
  "scope_4x",
  "scope_8x",
];

function hasOptic(mods: readonly string[]): boolean {
  return mods.some((m) => OPTIC_IDS.includes(m));
}

/**
 * Adds every fitted ADD-kind part's lump (optics, muzzle devices, foregrips,
 * rail furniture) at the anchors the components have moved into place. Swap
 * kinds were built as components in `mountSwaps`. `muzzle` is MOVED, not
 * replaced, so the viewmodel's parented flash survives. `scope` is set when a
 * magnifying optic was fitted — one per gun, so a single value.
 */
function applyMods(
  group: THREE.Group,
  mats: WeaponMaterials,
  mods: readonly string[],
  a: AttachAnchors,
  muzzle: THREE.Object3D,
): { extraLength: number; scope?: ScopeSurface } {
  let extraLength = 0;
  let scope: ScopeSurface | undefined;

  const add = (mesh: THREE.Mesh, x: number, y: number, z: number): void => {
    mesh.position.set(x, y, z);
    group.add(mesh);
  };

  /**
   * Ocular glass, reticle and eyepiece housing. `rearZ` is the tube's rear
   * face; from there toward the eye: housing flush, glass half a millimetre
   * nearer (so the housing cannot occlude it), then the reticle — so the spike
   * draws over the magnified world and the housing hides the picture's edge.
   */
  const addOcular = (
    rearZ: number,
    housing: { size: number; depth: number },
    lensR: number,
  ): void => {
    add(
      new THREE.Mesh(boxGeo(housing.size, housing.size, housing.depth), mats.sight),
      0,
      0,
      rearZ - housing.depth / 2,
    );
    const glass = lensDisc(lensR);
    mats.all.push(glass.mat);
    add(glass.mesh, 0, 0, rearZ + 0.0005);
    const mark = reticleQuad();
    mats.all.push(mark.mat);
    add(mark.mesh, 0, 0, rearZ + 0.001);
    scope = { lens: glass.mesh, radiusM: lensR };
  };

  for (const mod of mods) {
    switch (mod) {
      // --- Optik ----------------------------------------------------------
      case "red_dot": {
        // A window, not a tube: four bars around a hole with the dot on the
        // sight axis. Kept small — receivers top out a centimetre below the
        // axis, so a 3 cm ring would be half buried in the gun.
        const z = a.opticZ;
        // The mount stands ON the receiver's top face, however low that is: a
        // fixed 2.4 cm base cleared the hunting rifle's action by 3 mm.
        const baseBottom = Math.min(a.receiverY ?? -0.038, -0.038);
        const baseH = -0.014 - baseBottom;
        add(
          new THREE.Mesh(boxGeo(0.032, baseH, 0.048), mats.receiver),
          0,
          baseBottom + baseH / 2,
          z,
        );
        add(new THREE.Mesh(boxGeo(0.034, 0.006, 0.04), mats.sight), 0, 0.016, z);
        add(new THREE.Mesh(boxGeo(0.034, 0.006, 0.04), mats.sight), 0, -0.013, z);
        for (const x of [-0.014, 0.014]) {
          add(new THREE.Mesh(boxGeo(0.006, 0.035, 0.04), mats.sight), x, 0.0015, z);
        }
        // Unlit: a mark that dims in a doorway is not a mark.
        const dot = reticleQuad();
        mats.all.push(dot.mat);
        add(dot.mesh, 0, 0, z);
        break;
      }
      // The three magnifying scopes share one grammar — tube on the axis,
      // objective bell forward, two rings, eyepiece at the back — and differ
      // only in length and girth. All put the rear face at `a.opticZ + 0.08`:
      // the eye relief does not move with magnification.
      case "scope_2x": {
        // Short and slim — a stalking scope.
        const rearZ = a.opticZ + 0.08;
        const z = rearZ - 0.065;
        add(new THREE.Mesh(boxGeo(0.032, 0.032, 0.13), mats.sight), 0, 0, z);
        add(new THREE.Mesh(boxGeo(0.042, 0.042, 0.04), mats.sight), 0, 0, z - 0.075);
        add(new THREE.Mesh(boxGeo(0.042, 0.042, 0.005), mats.bead), 0, 0, z - 0.098);
        for (const dz of [-0.04, 0.035]) {
          add(new THREE.Mesh(boxGeo(0.026, 0.044, 0.018), mats.receiver), 0, -0.03, z + dz);
        }
        addOcular(rearZ, { size: 0.044, depth: 0.028 }, 0.02);
        break;
      }
      case "scope_4x": {
        // The 4x, same shape as the hunting rifle's factory glass.
        const rearZ = a.opticZ + 0.08;
        const z = rearZ - 0.1;
        add(new THREE.Mesh(boxGeo(0.038, 0.038, 0.2), mats.sight), 0, 0, z);
        add(new THREE.Mesh(boxGeo(0.05, 0.05, 0.055), mats.sight), 0, 0, z - 0.126);
        add(new THREE.Mesh(boxGeo(0.038, 0.038, 0.005), mats.bead), 0, 0, z - 0.156);
        for (const dz of [-0.07, 0.055]) {
          add(new THREE.Mesh(boxGeo(0.028, 0.044, 0.02), mats.receiver), 0, -0.03, z + dz);
        }
        // The lens disc (4.6 cm) is wider than the tube (3.8 cm); the housing
        // keeps the picture inside its own scope.
        addOcular(rearZ, { size: 0.05, depth: 0.03 }, 0.023);
        break;
      }
      case "scope_8x": {
        // The 8x: a quarter metre of tube and a 6 cm objective — unmistakable.
        const rearZ = a.opticZ + 0.08;
        const z = rearZ - 0.12;
        add(new THREE.Mesh(boxGeo(0.044, 0.044, 0.24), mats.sight), 0, 0, z);
        add(new THREE.Mesh(boxGeo(0.06, 0.06, 0.07), mats.sight), 0, 0, z - 0.145);
        add(new THREE.Mesh(boxGeo(0.06, 0.06, 0.005), mats.bead), 0, 0, z - 0.182);
        for (const dz of [-0.09, 0.05]) {
          add(new THREE.Mesh(boxGeo(0.03, 0.05, 0.022), mats.receiver), 0, -0.033, z + dz);
        }
        addOcular(rearZ, { size: 0.056, depth: 0.034 }, 0.026);
        break;
      }

      // --- Mündung --------------------------------------------------------
      case "muzzle_brake_8x57":
      case "ported_choke_12g": {
        // Short, wide and ported: a core block with two wider bands around it,
        // which reads as slots from any angle a player will ever see it at.
        const len = 0.05;
        add(new THREE.Mesh(boxGeo(0.048, 0.03, len), mats.barrel), 0, a.barrelY, a.muzzleZ - len / 2);
        for (const dz of [-0.014, 0.014]) {
          add(
            new THREE.Mesh(boxGeo(0.052, 0.036, 0.008), mats.barrel),
            0,
            a.barrelY,
            a.muzzleZ - len / 2 + dz,
          );
        }
        muzzle.position.z = a.muzzleZ - len - 0.008;
        extraLength += len;
        break;
      }
      case "suppressor_9x19":
      case "suppressor_8x57": {
        // A fat cylinder faked as a box plus a 45°-rolled box. The 8×57 can is
        // longer and fatter than the 9 mm one — the length is the read.
        const big = mod === "suppressor_8x57";
        const len = big ? 0.24 : 0.18;
        const girth = big ? 0.052 : 0.042;
        const cz = a.muzzleZ - len / 2;
        add(new THREE.Mesh(boxGeo(girth, girth, len), mats.barrel), 0, a.barrelY, cz);
        const roll = new THREE.Mesh(boxGeo(girth, girth, len - 0.004), mats.barrel);
        roll.rotation.z = Math.PI / 4;
        add(roll, 0, a.barrelY, cz);
        muzzle.position.z = a.muzzleZ - len - 0.01;
        extraLength += len;
        break;
      }

      // --- Griff ----------------------------------------------------------
      case "foregrip":
        add(
          new THREE.Mesh(boxGeo(0.03, 0.085, 0.032), mats.stock),
          0,
          a.forendY - 0.045,
          a.forendZ,
        );
        add(
          new THREE.Mesh(boxGeo(0.04, 0.014, 0.044), mats.receiver),
          0,
          a.forendY - 0.004,
          a.forendZ,
        );
        break;

      // --- Muzzle devices, Eastern thread ------------------------------------
      case "thread_protector": {
        // A nut. The smallest part in the game and the smallest mesh, and it
        // moves the anchor by its own thickness so the flash clears it.
        const len = 0.014;
        add(
          new THREE.Mesh(boxGeo(0.026, 0.026, len), mats.barrel),
          0,
          a.barrelY,
          a.muzzleZ - len / 2,
        );
        muzzle.position.z = a.muzzleZ - len - 0.004;
        extraLength += len;
        break;
      }
      case "slant_brake": {
        // The slant cut is the identity, rolled a few degrees so it reads
        // from the shooter's view too.
        const len = 0.045;
        const cz = a.muzzleZ - len / 2;
        add(new THREE.Mesh(boxGeo(0.03, 0.036, len), mats.barrel), 0, a.barrelY, cz);
        const slant = new THREE.Mesh(boxGeo(0.028, 0.03, 0.03), mats.barrel);
        slant.rotation.x = -0.5;
        add(slant, 0, a.barrelY + 0.018, cz - 0.008);
        muzzle.position.z = a.muzzleZ - len - 0.006;
        extraLength += len;
        break;
      }

      // --- Rail furniture: hangs off `tactical`, which on a rifle is a slot on
      // the handguard, but the anchor is the gun's — the fore-end is where the
      // handguard is.
      case "flashlight": {
        if (a.tacticalY === undefined || a.tacticalZ === undefined) break;
        const y = a.tacticalY;
        const z = a.tacticalZ;
        add(new THREE.Mesh(boxGeo(0.026, 0.026, 0.07), mats.barrel), 0, y, z);
        const roll = new THREE.Mesh(boxGeo(0.026, 0.026, 0.066), mats.barrel);
        roll.rotation.z = Math.PI / 4;
        add(roll, 0, y, z);
        // The lens, unlit like every other bright face in this file: the torch
        // throws no light in the valley yet, but it should still catch the eye.
        add(new THREE.Mesh(boxGeo(0.022, 0.022, 0.004), mats.bead), 0, y, z - 0.036);
        break;
      }
      case "laser": {
        if (a.tacticalY === undefined || a.tacticalZ === undefined) break;
        const y = a.tacticalY;
        const z = a.tacticalZ;
        add(new THREE.Mesh(boxGeo(0.022, 0.022, 0.05), mats.receiver), 0, y, z);
        add(new THREE.Mesh(boxGeo(0.012, 0.012, 0.004), mats.bead), 0, y, z - 0.026);
        break;
      }

      default:
        // A swap kind (already drawn as a component) or an unknown part;
        // `aggregateWeaponStats` ignores unknowns too.
        break;
    }
  }

  return { extraLength, scope };
}

/** Base overall length; the fitted barrel and muzzle device add to it. */
const LENGTH_M = 0.7;

/** The carbine hangs this far below its own sight axis. */
const CARBINE_BODY_Y = -0.055;

/**
 * Anchors for the carbine. Unused kinds are filled honestly so leader lines
 * land on the gun.
 */
const CARBINE_ANCHORS: AttachAnchors = {
  barrelY: -0.037,
  muzzleZ: -0.412,
  forendY: -0.087,
  forendZ: -0.165,
  magY: -0.191,
  magZ: -0.02,
  buttY: -0.067,
  buttZ: 0.28,
  opticZ: -0.05,
  receiverY: -0.014,
  receiverZ: -0.015,
  barrelZ: -0.24,
  handguardY: -0.061,
  handguardZ: -0.14,
  pistolgripY: -0.143,
  pistolgripZ: 0.075,
  tacticalY: -0.09,
  tacticalZ: -0.215,
};

/** Trigger hand high on the raked grip; support hand around the wooden
 *  fore-end, which is the widest thing forward of the receiver. */
const CARBINE_GRIPS: GripPoints = {
  triggerY: -0.115,
  triggerZ: 0.062,
  supportY: -0.062,
  supportZ: -0.165,
};

// --- The carbine's components ----------------------------------------------
//
// Three detachable swap slots (`carbine_stock`, `carbine_barrel`, the well).
// The fore-end belongs to the STOCK: on a hunting carbine butt, comb and
// fore-end are one piece of walnut, and a thumbhole stock replaces all three.

function carbineWoodStock(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  const y = CARBINE_BODY_Y;
  put(g, new THREE.Mesh(boxGeo(0.052, 0.115, 0.09), mats.stock), 0, y - 0.012, 0.235);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.072, 0.2), mats.stock), 0, y, 0.1);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.052, 0.22), mats.stock), 0, y - 0.006, -0.14);
  return { group: g, buttY: y - 0.012, buttZ: 0.28 };
}

function carbinePaddedStock(mats: WeaponMaterials): WeaponComponent {
  // The same walnut with 2.4 cm of rubber: a recoil pad is a whole stock
  // (`attachKind: "stock"`).
  const c = carbineWoodStock(mats);
  put(c.group, new THREE.Mesh(boxGeo(0.064, 0.135, 0.024), mats.receiver), 0, -0.067, 0.292);
  return { ...c, buttZ: 0.304 };
}

function carbineSkeletonStock(mats: WeaponMaterials): WeaponComponent {
  // Two rails with a strut across the wrist, a butt plate and a slimmer
  // polymer fore-end.
  const g = new THREE.Group();
  const buttY = -0.067;
  const z = 0.205;
  // The wrist: a solid block from the receiver's rear face (z = 0.085) back to
  // the frame's front strut. Without it the frame hung 3 cm behind the gun.
  put(g, new THREE.Mesh(boxGeo(0.042, 0.07, 0.06), mats.receiver), 0, CARBINE_BODY_Y, 0.1);
  for (const dy of [0.058, -0.052]) {
    put(g, new THREE.Mesh(boxGeo(0.028, 0.016, 0.17), mats.receiver), 0, buttY + dy, z);
  }
  put(g, new THREE.Mesh(boxGeo(0.026, 0.11, 0.018), mats.receiver), 0, buttY, z - 0.083);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.14, 0.02), mats.receiver), 0, buttY, 0.29);
  put(g, new THREE.Mesh(boxGeo(0.046, 0.048, 0.22), mats.receiver), 0, CARBINE_BODY_Y - 0.006, -0.14);
  return { group: g, buttY, buttZ: 0.3 };
}

function carbineBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  const g = new THREE.Group();
  const y = CARBINE_BODY_Y + 0.018;
  put(g, new THREE.Mesh(boxGeo(0.026, 0.026, 0.34), mats.barrel), 0, y, -0.24);
  if (ctx.irons) frontSight(g, mats, 0.022, -0.39);
  return { group: g, muzzleZ: -0.412 };
}

function carbineHuntingBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // 12 cm longer; the breech end is fixed, so the extra goes out the front
  // with the front sight and muzzle.
  const g = new THREE.Group();
  const y = CARBINE_BODY_Y + 0.018;
  put(g, new THREE.Mesh(boxGeo(0.026, 0.026, 0.46), mats.barrel), 0, y, -0.3);
  if (ctx.irons) frontSight(g, mats, 0.022, -0.51);
  return { group: g, muzzleZ: -0.532 };
}

function carbineMagazine(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  const box = put(g, new THREE.Mesh(boxGeo(0.038, 0.1, 0.06), mats.receiver), 0, -0.141, -0.02);
  box.rotation.x = -0.12;
  put(g, new THREE.Mesh(boxGeo(0.042, 0.014, 0.06), mats.receiver), 0, -0.188, -0.02);
  return { group: g, magY: -0.195, magZ: -0.02, magMesh: g };
}

function carbineExtendedMagazine(mats: WeaponMaterials): WeaponComponent {
  // The same box, 6 cm deeper — one tall box, not a lump under the factory one.
  const g = new THREE.Group();
  const box = put(g, new THREE.Mesh(boxGeo(0.038, 0.16, 0.06), mats.receiver), 0, -0.171, -0.02);
  box.rotation.x = -0.12;
  put(g, new THREE.Mesh(boxGeo(0.042, 0.014, 0.06), mats.receiver), 0, -0.248, -0.02);
  return { group: g, magY: -0.255, magZ: -0.02, magMesh: g };
}

const CARBINE_SWAPS: WeaponSwaps = {
  stock: {
    factory: carbineWoodStock,
    factoryKind: "detachable",
    parts: {
      carbine_stock: carbineWoodStock,
      recoil_pad: carbinePaddedStock,
      thumbhole_stock: carbineSkeletonStock,
    },
  },
  barrel: {
    factory: carbineBarrel,
    factoryKind: "detachable",
    // `factory` still names `carbineBarrel` so an unknown id degrades to the
    // factory look (see `SwapSlot.parts`).
    parts: { carbine_barrel: carbineBarrel, carbine_barrel_long: carbineHuntingBarrel },
    // No barrel: the flash comes out of the front face of the receiver, which is
    // where the tube screws in and therefore where the hole is.
    emptyMuzzleZ: -0.12,
  },
  mag: {
    factory: carbineMagazine,
    factoryKind: "detachable",
    parts: {
      carbine_mag_30: carbineMagazine,
      carbine_mag_40: carbineExtendedMagazine,
    },
  },
};

function buildCarbine(mods: readonly string[], bare: BareSlotView): BuiltWeapon {
  const group = new THREE.Group();
  group.name = "weapon:carbine";

  const mats = makeMaterials();
  const receiverMat = mats.receiver;
  const sightMat = mats.sight;

  // The sight axis is y = 0; everything hangs below it, which is what makes the
  // ADS pose a plain "centre the group" instead of a tuned offset.
  const SIGHT_Y = 0;
  const BODY_Y = CARBINE_BODY_Y;
  const irons = !hasOptic(mods);

  // The build's OWN anchors: the components below move some of these, and the
  // shared constant must survive it. See `mountSwaps`.
  const a: AttachAnchors = { ...CARBINE_ANCHORS };

  // --- Dark receiver and grip: the gun itself in every configuration ---------
  const receiver = new THREE.Mesh(boxGeo(0.046, 0.078, 0.2), receiverMat);
  receiver.position.set(0, BODY_Y + 0.006, -0.015);
  group.add(receiver);

  const bolt = new THREE.Mesh(boxGeo(0.03, 0.022, 0.06), receiverMat);
  // Right-hand bolt handle, offset off the receiver's side.
  bolt.position.set(0.036, BODY_Y + 0.028, 0.01);
  group.add(bolt);

  const grip = new THREE.Mesh(boxGeo(0.042, 0.12, 0.055), receiverMat);
  grip.position.set(0, BODY_Y - 0.088, 0.075);
  grip.rotation.x = 0.24;
  group.add(grip);

  const trigger = new THREE.Mesh(boxGeo(0.014, 0.03, 0.014), receiverMat);
  trigger.position.set(0, BODY_Y - 0.042, 0.035);
  group.add(trigger);

  // --- Stock, barrel and magazine: whichever ones are on it -----------------
  const fittedParts = mountSwaps(group, mats, CARBINE_SWAPS, mods, bare, a, irons);

  // --- Iron sights ----------------------------------------------------------
  // The references sit exactly ON the sight axis (y = SIGHT_Y): rear post TOPS
  // touch it from underneath (centring them on it zeroes the gun low), bead
  // CENTRE on it. Skipped once an optic is fitted — it claims the same axis.
  // Only the rear notch is here; the front sight is on the barrel
  // (`frontSight`).
  if (irons) {
    const rearBase = new THREE.Mesh(boxGeo(0.036, 0.012, 0.026), sightMat);
    rearBase.position.set(0, SIGHT_Y - 0.021, -0.08);
    group.add(rearBase);

    const rearL = new THREE.Mesh(boxGeo(0.008, 0.018, 0.02), sightMat);
    rearL.position.set(-0.012, SIGHT_Y - 0.009, -0.08);
    group.add(rearL);

    const rearR = new THREE.Mesh(boxGeo(0.008, 0.018, 0.02), sightMat);
    rearR.position.set(0.012, SIGHT_Y - 0.009, -0.08);
    group.add(rearR);
  }

  // --- Muzzle anchor, then whatever is bolted on ----------------------------
  const muzzle = addMuzzle(group, a.barrelY, a.muzzleZ);
  const fitted = applyMods(group, mats, mods, a, muzzle);
  const lengthM = LENGTH_M + (CARBINE_ANCHORS.muzzleZ - a.muzzleZ) + fitted.extraLength;
  return finish(group, mats, muzzle, lengthM, a, {
    grips: CARBINE_GRIPS,
    magMesh: fittedParts.magMesh,
    scope: fitted.scope,
  });
}

// ---------------------------------------------------------------------------

/**
 * Maschinenpistole 9 — short, boxy, folding wire stock, 25-round magazine
 * straight down out of the grip. Anchors: the only host for the can, the stick,
 * the drum, the railed fore-end and the light under it, so `handguard` and
 * `tactical` are real geometry here.
 */
const SMG_ANCHORS: AttachAnchors = {
  barrelY: -0.038,
  muzzleZ: -0.282,
  forendY: -0.059,
  forendZ: -0.2,
  magY: -0.29,
  magZ: -0.005,
  buttY: -0.062,
  buttZ: 0.255,
  opticZ: -0.035,
  receiverY: -0.008,
  receiverZ: -0.02,
  barrelZ: -0.21,
  // Over the ribbed section, which is the fore-end a polymer shell replaces.
  handguardY: -0.038,
  handguardZ: -0.2,
  tacticalY: -0.072,
  tacticalZ: -0.225,
  pistolgripY: -0.14,
  pistolgripZ: 0.06,
};

/** Support hand cups the shroud from BELOW (fist centre under the tube) rather
 *  than the magazine — the stick is the silhouette — and stays out of the sight
 *  line, which sits nearest the fore-end on this gun. See `HAND_FLOOR_Y`. */
const SMG_GRIPS: GripPoints = {
  triggerY: -0.112,
  triggerZ: 0.055,
  supportY: -0.065,
  supportZ: -0.2,
};

/** The MP 9's body height below the sight axis. */
const SMG_BODY_Y = -0.05;

// --- The MP 9's components ---------------------------------------------------
//
// Four detachable swap slots (`wire_stock`, `smg_barrel`, `poly_foreend`, the
// well). The shroud is the only fore-end that can end up longer than the
// barrel inside it (the stub is 8 cm shorter), so both handguard components
// stop short of whatever muzzle the barrel reported.

function smgWireStock(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  const y = SMG_BODY_Y - 0.012;
  for (const x of [-0.022, 0.022]) {
    put(g, new THREE.Mesh(boxGeo(0.008, 0.008, 0.14), mats.receiver), x, y, 0.175);
  }
  put(g, new THREE.Mesh(boxGeo(0.06, 0.05, 0.014), mats.receiver), 0, y, 0.248);
  return { group: g, buttY: y, buttZ: 0.255 };
}

function smgPaddedStock(mats: WeaponMaterials): WeaponComponent {
  const c = smgWireStock(mats);
  put(c.group, new THREE.Mesh(boxGeo(0.064, 0.07, 0.024), mats.receiver), 0, -0.062, 0.267);
  return { ...c, buttZ: 0.279 };
}

function smgSkeletonStock(mats: WeaponMaterials): WeaponComponent {
  // A fixed thumbhole frame in place of the folding wire: heavier, steadier,
  // and visibly a different stock rather than a cage bolted over the old one.
  const g = new THREE.Group();
  const buttY = -0.062;
  for (const dy of [0.05, -0.05]) {
    put(g, new THREE.Mesh(boxGeo(0.026, 0.014, 0.16), mats.receiver), 0, buttY + dy, 0.19);
  }
  put(g, new THREE.Mesh(boxGeo(0.024, 0.1, 0.016), mats.receiver), 0, buttY, 0.115);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.13, 0.018), mats.receiver), 0, buttY, 0.278);
  return { group: g, buttY, buttZ: 0.287 };
}

function smgBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  return smgBarrelOfLength(mats, ctx, 0.14);
}

function smgStubBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  return smgBarrelOfLength(mats, ctx, 0.06);
}

function smgCarbineBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  return smgBarrelOfLength(mats, ctx, 0.24);
}

/** All three MP 9 barrels are the same tube at three lengths, breech end fixed
 *  where it screws into the receiver. Length IS the part. */
function smgBarrelOfLength(
  mats: WeaponMaterials,
  ctx: ComponentCtx,
  len: number,
): WeaponComponent {
  const g = new THREE.Group();
  const y = SMG_BODY_Y + 0.012;
  const zRear = -0.14;
  put(g, new THREE.Mesh(boxGeo(0.024, 0.024, len), mats.barrel), 0, y, zRear - len / 2);
  const muzzleZ = zRear - len - 0.002;
  if (ctx.irons) frontSight(g, mats, 0.026, muzzleZ + 0.022);
  return { group: g, muzzleZ };
}

function smgRibbedShroud(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // A perforated barrel shroud, suggested with three ribs — and only the ribs
  // that are still over a barrel. A stub leaves one.
  const g = new THREE.Group();
  const y = SMG_BODY_Y + 0.012;
  for (const z of [-0.16, -0.2, -0.24]) {
    if (z <= ctx.muzzleZ + 0.02) continue;
    put(g, new THREE.Mesh(boxGeo(0.042, 0.042, 0.012), mats.receiver), 0, y, z);
  }
  return { group: g };
}

function smgRailedForeEnd(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // A polymer shell with one short rail underneath — less hardware than the
  // rifle's.
  const g = new THREE.Group();
  const y = SMG_BODY_Y + 0.012;
  const span = spanToMuzzle(-0.14, -0.26, ctx.muzzleZ, 0.025);
  put(g, new THREE.Mesh(boxGeo(0.046, 0.046, span.depth), mats.receiver), 0, y, span.center);
  put(
    g,
    new THREE.Mesh(boxGeo(0.018, 0.008, Math.max(0.03, span.depth - 0.05)), mats.sight),
    0,
    y - 0.026,
    span.center,
  );
  return { group: g };
}

function smgStickMagazine(mats: WeaponMaterials): WeaponComponent {
  // THE feature: 20 cm of straight box, dead vertical, and the one thing that
  // reads on this gun at any range.
  const g = new THREE.Group();
  put(g, new THREE.Mesh(boxGeo(0.034, 0.2, 0.05), mats.receiver), 0, SMG_BODY_Y - 0.14, -0.005);
  put(g, new THREE.Mesh(boxGeo(0.038, 0.012, 0.052), mats.receiver), 0, -0.284, -0.005);
  return { group: g, magY: -0.29, magZ: -0.005, magMesh: g };
}

function smgDrumMagazine(mats: WeaponMaterials): WeaponComponent {
  // A drum: feed neck, then the suppressor's box-plus-rolled-box cylinder on
  // its side. The widest thing on any gun — a player should see it coming.
  const g = new THREE.Group();
  put(g, new THREE.Mesh(boxGeo(0.034, 0.06, 0.05), mats.receiver), 0, SMG_BODY_Y - 0.07, -0.005);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.11, 0.11), mats.receiver), 0, -0.2, -0.005);
  const roll = new THREE.Mesh(boxGeo(0.048, 0.11, 0.11), mats.receiver);
  roll.rotation.x = Math.PI / 4;
  put(g, roll, 0, -0.2, -0.005);
  return { group: g, magY: -0.278, magZ: -0.005, magMesh: g };
}

const SMG_SWAPS: WeaponSwaps = {
  stock: {
    factory: smgWireStock,
    factoryKind: "detachable",
    parts: {
      wire_stock: smgWireStock,
      recoil_pad: smgPaddedStock,
      thumbhole_stock: smgSkeletonStock,
    },
  },
  barrel: {
    factory: smgBarrel,
    factoryKind: "detachable",
    parts: {
      smg_barrel: smgBarrel,
      smg_barrel_short: smgStubBarrel,
      smg_barrel_long: smgCarbineBarrel,
    },
    // The front face of the boxy receiver, 1 cm ahead of the barrel nut every
    // one of the three tubes screws into.
    emptyMuzzleZ: -0.155,
  },
  handguard: {
    factory: smgRibbedShroud,
    factoryKind: "detachable",
    parts: { poly_foreend: smgRibbedShroud, smg_rail_foreend: smgRailedForeEnd },
  },
  mag: {
    factory: smgStickMagazine,
    factoryKind: "detachable",
    parts: { smg_mag_25: smgStickMagazine, smg_drum_45: smgDrumMagazine },
  },
};

function buildSmg(mods: readonly string[], bare: BareSlotView): BuiltWeapon {
  const group = new THREE.Group();
  group.name = "weapon:smg";

  const mats = makeMaterials();
  const SIGHT_Y = 0;
  const BODY_Y = SMG_BODY_Y;
  const irons = !hasOptic(mods);
  const a: AttachAnchors = { ...SMG_ANCHORS };

  // --- Boxy receiver, the whole gun really -----------------------------------
  const receiver = new THREE.Mesh(boxGeo(0.05, 0.088, 0.26), mats.receiver);
  receiver.position.set(0, BODY_Y, -0.02);
  group.add(receiver);

  const grip = new THREE.Mesh(boxGeo(0.04, 0.1, 0.05), mats.receiver);
  grip.position.set(0, BODY_Y - 0.09, 0.06);
  grip.rotation.x = 0.2;
  group.add(grip);

  // --- Stock, barrel, shroud and magazine ------------------------------------
  const fittedParts = mountSwaps(group, mats, SMG_SWAPS, mods, bare, a, irons);

  // --- Sights: same contract as the carbine, tops/centre on y = 0 ------------
  // Rear notch only; the front post rides the barrel (see `frontSight`).
  if (irons) {
    const rearL = new THREE.Mesh(boxGeo(0.008, 0.02, 0.016), mats.sight);
    rearL.position.set(-0.013, SIGHT_Y - 0.01, -0.05);
    group.add(rearL);
    const rearR = new THREE.Mesh(boxGeo(0.008, 0.02, 0.016), mats.sight);
    rearR.position.set(0.013, SIGHT_Y - 0.01, -0.05);
    group.add(rearR);
  }

  const muzzle = addMuzzle(group, a.barrelY, a.muzzleZ);
  const fitted = applyMods(group, mats, mods, a, muzzle);
  const lengthM = 0.5 + (SMG_ANCHORS.muzzleZ - a.muzzleZ) + fitted.extraLength;
  return finish(group, mats, muzzle, lengthM, a, {
    grips: SMG_GRIPS,
    magMesh: fittedParts.magMesh,
    scope: fitted.scope,
  });
}

// ---------------------------------------------------------------------------

/**
 * Pump-action shotgun: a magazine tube slung under the barrel and a ribbed
 * sliding fore-end carry the silhouette. No rear sight — aimed with the bead.
 * Anchors: its `mag` slot extends the tube, not a box.
 */
const SHOTGUN_ANCHORS: AttachAnchors = {
  barrelY: -0.034,
  muzzleZ: -0.535,
  forendY: -0.091,
  forendZ: -0.21,
  magY: -0.076,
  magZ: -0.29,
  buttY: -0.078,
  buttZ: 0.34,
  opticZ: -0.03,
  tubeY: -0.064,
  tubeZ: -0.47,
  receiverY: -0.013,
  receiverZ: -0.01,
  barrelZ: -0.32,
  handguardY: -0.066,
  handguardZ: -0.21,
  pistolgripY: -0.1,
  pistolgripZ: 0.078,
  tacticalY: -0.096,
  tacticalZ: -0.28,
};

/**
 * No pistol grip: trigger hand on the wrist of the stock, support hand on the
 * pump.
 */
const SHOTGUN_GRIPS: GripPoints = {
  triggerY: -0.078,
  triggerZ: 0.078,
  supportY: -0.066,
  supportZ: -0.21,
};

/** The shotgun's body height below the sight axis. */
const SHOTGUN_BODY_Y = -0.06;

// --- The shotgun's components -------------------------------------------------
//
// Three detachable swap slots: `field_stock`, `shotgun_barrel`, and the `mag`
// slot, which is the magazine TUBE (a detachable magazine that cannot be
// swapped mid-fight — `reloadStyle: "topup"`). The PUMP is base geometry in
// `buildShotgun`: its `grip` slot takes a foregrip and nothing replaces the
// sliding block. Both tubes are clamped to the fitted barrel, so a coach gun
// no longer has 13 cm of magazine past its muzzle. No `magMesh` — nothing to
// drop.

function shotgunWoodStock(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  const y = SHOTGUN_BODY_Y;
  put(g, new THREE.Mesh(boxGeo(0.055, 0.13, 0.1), mats.stock), 0, y - 0.018, 0.29);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.075, 0.22), mats.stock), 0, y - 0.002, 0.14);
  return { group: g, buttY: y - 0.018, buttZ: 0.34 };
}

function shotgunPaddedStock(mats: WeaponMaterials): WeaponComponent {
  const c = shotgunWoodStock(mats);
  put(c.group, new THREE.Mesh(boxGeo(0.066, 0.15, 0.024), mats.receiver), 0, -0.078, 0.352);
  return { ...c, buttZ: 0.364 };
}

function shotgunSkeletonStock(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  const buttY = -0.078;
  // Wrist block from the receiver's rear face (z = 0.085) to the frame's front
  // strut; the frame used to start 8 cm behind the gun with nothing between.
  put(g, new THREE.Mesh(boxGeo(0.044, 0.07, 0.11), mats.receiver), 0, SHOTGUN_BODY_Y, 0.13);
  for (const dy of [0.055, -0.055]) {
    put(g, new THREE.Mesh(boxGeo(0.028, 0.016, 0.18), mats.receiver), 0, buttY + dy, 0.265);
  }
  put(g, new THREE.Mesh(boxGeo(0.026, 0.12, 0.018), mats.receiver), 0, buttY, 0.178);
  put(g, new THREE.Mesh(boxGeo(0.052, 0.15, 0.02), mats.receiver), 0, buttY, 0.35);
  return { group: g, buttY, buttZ: 0.36 };
}

function shotgunBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  const g = new THREE.Group();
  const y = SHOTGUN_BODY_Y + 0.026;
  put(g, new THREE.Mesh(boxGeo(0.03, 0.03, 0.42), mats.barrel), 0, y, -0.32);
  // A shotgun is aimed with the bead and nothing else — no rear notch anywhere
  // on this gun, which is one more silhouette cue.
  if (ctx.irons) frontSight(g, mats, 0.018, -0.5);
  return { group: g, muzzleZ: -0.535 };
}

function shotgunCoachBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // Cut down to 23 cm and opened up — the one part that makes a gun worse on
  // purpose, and it should look like it.
  const g = new THREE.Group();
  const y = SHOTGUN_BODY_Y + 0.026;
  put(g, new THREE.Mesh(boxGeo(0.034, 0.034, 0.23), mats.barrel), 0, y, -0.225);
  put(g, new THREE.Mesh(boxGeo(0.038, 0.038, 0.012), mats.barrel), 0, y, -0.334);
  if (ctx.irons) frontSight(g, mats, 0.018, -0.31);
  return { group: g, muzzleZ: -0.345 };
}

/** Both tubes: same cylinder under the barrel, different reach. `zWanted` is
 *  where it would end on a full-length gun; `spanToMuzzle` decides the rest. */
function shotgunTube(
  mats: WeaponMaterials,
  ctx: ComponentCtx,
  zWanted: number,
): WeaponComponent {
  const g = new THREE.Group();
  const y = SHOTGUN_BODY_Y - 0.004;
  const span = spanToMuzzle(-0.11, zWanted, ctx.muzzleZ, 0.005, 0.12);
  put(g, new THREE.Mesh(boxGeo(0.024, 0.024, span.depth), mats.barrel), 0, y, span.center);
  const tip = -0.11 - span.depth;
  put(g, new THREE.Mesh(boxGeo(0.032, 0.032, 0.012), mats.barrel), 0, y, tip + 0.006);
  return { group: g, tubeY: y, tubeZ: tip };
}

function shotgunFactoryTube(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  return shotgunTube(mats, ctx, -0.47);
}

function shotgunLongTube(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  return shotgunTube(mats, ctx, -0.53);
}

const SHOTGUN_SWAPS: WeaponSwaps = {
  stock: {
    factory: shotgunWoodStock,
    factoryKind: "detachable",
    parts: {
      field_stock: shotgunWoodStock,
      recoil_pad: shotgunPaddedStock,
      thumbhole_stock: shotgunSkeletonStock,
    },
  },
  barrel: {
    factory: shotgunBarrel,
    factoryKind: "detachable",
    parts: { shotgun_barrel: shotgunBarrel, shotgun_barrel_coach: shotgunCoachBarrel },
    // The breech, a hair ahead of the front face of the receiver: both tubes
    // start at −0.11, which is where the barrel is pinned to the action.
    emptyMuzzleZ: -0.115,
  },
  mag: {
    factory: shotgunFactoryTube,
    factoryKind: "detachable",
    parts: { shotgun_tube_5: shotgunFactoryTube, shotgun_tube_8: shotgunLongTube },
  },
};

function buildShotgun(mods: readonly string[], bare: BareSlotView): BuiltWeapon {
  const group = new THREE.Group();
  group.name = "weapon:shotgun";

  const mats = makeMaterials();
  const BODY_Y = SHOTGUN_BODY_Y;
  const irons = !hasOptic(mods);
  const a: AttachAnchors = { ...SHOTGUN_ANCHORS };

  const receiver = new THREE.Mesh(boxGeo(0.05, 0.086, 0.19), mats.receiver);
  receiver.position.set(0, BODY_Y + 0.004, -0.01);
  group.add(receiver);

  const trigger = new THREE.Mesh(boxGeo(0.014, 0.03, 0.014), mats.receiver);
  trigger.position.set(0, BODY_Y - 0.046, 0.045);
  group.add(trigger);

  // --- Pump: a ribbed block riding the tube. Base geometry, not a component
  // ---
  const pump = new THREE.Mesh(boxGeo(0.056, 0.05, 0.16), mats.stock);
  pump.position.set(0, BODY_Y - 0.006, -0.21);
  group.add(pump);
  for (const z of [-0.26, -0.23, -0.2, -0.17]) {
    const rib = new THREE.Mesh(boxGeo(0.06, 0.054, 0.008), mats.stock);
    rib.position.set(0, BODY_Y - 0.006, z);
    group.add(rib);
  }

  // --- Stock, barrel and tube ------------------------------------------------
  // The bead comes with the barrel; there is no rear sight to draw.
  mountSwaps(group, mats, SHOTGUN_SWAPS, mods, bare, a, irons);

  const muzzle = addMuzzle(group, a.barrelY, a.muzzleZ);
  const fitted = applyMods(group, mats, mods, a, muzzle);
  // No `magMesh`: the shotgun is fed one shell at a time into a fixed tube, so
  // there is nothing to drop out of it. See `WeaponItemDef.reloadStyle`.
  const lengthM = 0.85 + (SHOTGUN_ANCHORS.muzzleZ - a.muzzleZ) + fitted.extraLength;
  return finish(group, mats, muzzle, lengthM, a, {
    grips: SHOTGUN_GRIPS,
    scope: fitted.scope,
  });
}

// ---------------------------------------------------------------------------

/**
 * Hunting rifle — the bolt-action marksman rifle, longest of the v1 guns and
 * the only one with glass. The sight axis is the scope's optical axis, so the
 * whole rifle hangs further below y = 0 than any other model. Anchors: every
 * entry filled honestly (no tube — it has none).
 */
const DMR_ANCHORS: AttachAnchors = {
  barrelY: -0.065,
  muzzleZ: -0.645,
  forendY: -0.121,
  forendZ: -0.24,
  magY: -0.177,
  magZ: -0.03,
  buttY: -0.099,
  buttZ: 0.355,
  opticZ: -0.06,
  receiverY: -0.041,
  receiverZ: -0.03,
  barrelZ: -0.44,
  handguardY: -0.093,
  handguardZ: -0.24,
  pistolgripY: -0.175,
  pistolgripZ: 0.075,
  tacticalY: -0.126,
  tacticalZ: -0.3,
};

/** The rifle hangs furthest below its own sight axis (scope over bore), so both
 *  hands sit lower here than on anything else in the roster. */
const DMR_GRIPS: GripPoints = {
  triggerY: -0.145,
  triggerZ: 0.066,
  supportY: -0.095,
  supportZ: -0.235,
};

/** Scope over bore: the rifle's body hangs further below its own sight axis
 *  than anything else in the roster. */
const DMR_BODY_Y = -0.085;

// --- The hunting rifle's components --------------------------------------------
//
// Three detachable swap slots (`walnut_stock`, `rifle_barrel`, the well); the
// fore-end travels with the stock as on the carbine. The factory glass is an
// add-kind optic drawn by the scope block in `buildDmr`, not a component.

function dmrWoodStock(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  const y = DMR_BODY_Y;
  put(g, new THREE.Mesh(boxGeo(0.055, 0.125, 0.11), mats.stock), 0, y - 0.014, 0.3);
  // Raised comb: you cannot get behind a scope with a straight stock.
  put(g, new THREE.Mesh(boxGeo(0.052, 0.086, 0.24), mats.stock), 0, y + 0.006, 0.14);
  put(g, new THREE.Mesh(boxGeo(0.052, 0.056, 0.26), mats.stock), 0, y - 0.008, -0.24);
  return { group: g, buttY: y - 0.014, buttZ: 0.355 };
}

function dmrPaddedStock(mats: WeaponMaterials): WeaponComponent {
  const c = dmrWoodStock(mats);
  put(c.group, new THREE.Mesh(boxGeo(0.066, 0.145, 0.024), mats.receiver), 0, -0.099, 0.367);
  return { ...c, buttZ: 0.379 };
}

function dmrSkeletonStock(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  const buttY = -0.099;
  // Wrist block from the receiver's rear face (z = 0.08) to the frame's front
  // strut; the frame used to start 10 cm behind the action with nothing between.
  put(g, new THREE.Mesh(boxGeo(0.044, 0.07, 0.13), mats.receiver), 0, DMR_BODY_Y, 0.135);
  for (const dy of [0.06, -0.055]) {
    put(g, new THREE.Mesh(boxGeo(0.028, 0.016, 0.18), mats.receiver), 0, buttY + dy, 0.28);
  }
  put(g, new THREE.Mesh(boxGeo(0.026, 0.12, 0.018), mats.receiver), 0, buttY, 0.192);
  put(g, new THREE.Mesh(boxGeo(0.052, 0.15, 0.02), mats.receiver), 0, buttY, 0.365);
  put(g, new THREE.Mesh(boxGeo(0.048, 0.052, 0.26), mats.receiver), 0, DMR_BODY_Y - 0.008, -0.24);
  return { group: g, buttY, buttZ: 0.375 };
}

function dmrBarrel(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  put(g, new THREE.Mesh(boxGeo(0.026, 0.026, 0.51), mats.barrel), 0, DMR_BODY_Y + 0.02, -0.385);
  return { group: g, muzzleZ: -0.645 };
}

function dmrMatchBarrel(mats: WeaponMaterials): WeaponComponent {
  // A bull barrel: same length as the sporter, half again as fat. No irons on
  // this rifle at any length.
  const g = new THREE.Group();
  const y = DMR_BODY_Y + 0.02;
  put(g, new THREE.Mesh(boxGeo(0.04, 0.04, 0.51), mats.barrel), 0, y, -0.385);
  put(g, new THREE.Mesh(boxGeo(0.044, 0.044, 0.014), mats.barrel), 0, y, -0.632);
  return { group: g, muzzleZ: -0.645 };
}

function dmrFlushMagazine(mats: WeaponMaterials): WeaponComponent {
  // Five rounds, flush-fitting: the whole point of the part is that you cannot
  // see it from thirty metres.
  const g = new THREE.Group();
  put(g, new THREE.Mesh(boxGeo(0.036, 0.06, 0.062), mats.receiver), 0, -0.147, -0.03);
  put(g, new THREE.Mesh(boxGeo(0.04, 0.012, 0.062), mats.receiver), 0, -0.181, -0.03);
  return { group: g, magY: -0.187, magZ: -0.03, magMesh: g };
}

function dmrDeepMagazine(mats: WeaponMaterials): WeaponComponent {
  // Ten, hanging a hand's width below the stock — the one thing that spoils a
  // hunting rifle's outline, which is what it costs.
  const g = new THREE.Group();
  put(g, new THREE.Mesh(boxGeo(0.036, 0.105, 0.062), mats.receiver), 0, -0.1695, -0.03);
  put(g, new THREE.Mesh(boxGeo(0.04, 0.012, 0.062), mats.receiver), 0, -0.226, -0.03);
  return { group: g, magY: -0.232, magZ: -0.03, magMesh: g };
}

const DMR_SWAPS: WeaponSwaps = {
  stock: {
    factory: dmrWoodStock,
    factoryKind: "detachable",
    parts: {
      walnut_stock: dmrWoodStock,
      recoil_pad: dmrPaddedStock,
      thumbhole_stock: dmrSkeletonStock,
    },
  },
  barrel: {
    factory: dmrBarrel,
    factoryKind: "detachable",
    parts: { rifle_barrel: dmrBarrel, rifle_barrel_match: dmrMatchBarrel },
    // The receiver's front face, where both tubes thread in.
    emptyMuzzleZ: -0.145,
  },
  mag: {
    factory: dmrFlushMagazine,
    factoryKind: "detachable",
    parts: { rifle_mag_5: dmrFlushMagazine, rifle_mag_10: dmrDeepMagazine },
  },
};

/**
 * The hunting rifle's own 4× glass. Pulled out of `buildDmr` so `buildPart` can
 * draw `factory_scope_4x` alone for its tile; the caller decides whether to
 * call it.
 */
function dmrFactoryScope(group: THREE.Group, mats: WeaponMaterials): ScopeSurface {
  // The sight axis. A local const rather than a parameter: it is 0 by the
  // file's local-space contract, and naming it is what makes the block read.
  const SIGHT_Y = 0;

  const tube = new THREE.Mesh(boxGeo(0.042, 0.042, 0.28), mats.sight);
  tube.position.set(0, SIGHT_Y, -0.06);
  group.add(tube);

  const bell = new THREE.Mesh(boxGeo(0.056, 0.056, 0.07), mats.sight);
  bell.position.set(0, SIGHT_Y, -0.225);
  group.add(bell);

  const objective = new THREE.Mesh(boxGeo(0.042, 0.042, 0.006), mats.bead);
  objective.position.set(0, SIGHT_Y, -0.259);
  group.add(objective);

  // The ocular at the tube's rear face: the same sandwich `addOcular` builds
  // (hardware, glass, reticle). No eyepiece flare — the tube (4.2 cm) encloses
  // the disc (4.0). Only the own viewmodel renders into it; a remote player's
  // stays dark glass.
  const glass = lensDisc(0.02);
  mats.all.push(glass.mat);
  glass.mesh.position.set(0, SIGHT_Y, 0.0805);
  group.add(glass.mesh);
  const surface: ScopeSurface = { lens: glass.mesh, radiusM: 0.02 };

  const scopeMark = reticleQuad();
  mats.all.push(scopeMark.mat);
  scopeMark.mesh.position.set(0, SIGHT_Y, 0.081);
  group.add(scopeMark.mesh);

  for (const z of [-0.15, 0.03]) {
    const ring = new THREE.Mesh(boxGeo(0.03, 0.05, 0.022), mats.receiver);
    ring.position.set(0, SIGHT_Y - 0.033, z);
    group.add(ring);
  }
  return surface;
}

function buildDmr(mods: readonly string[], bare: BareSlotView): BuiltWeapon {
  const group = new THREE.Group();
  group.name = "weapon:dmr";

  const mats = makeMaterials();
  const BODY_Y = DMR_BODY_Y;
  const a: AttachAnchors = { ...DMR_ANCHORS };

  const receiver = new THREE.Mesh(boxGeo(0.048, 0.08, 0.22), mats.receiver);
  receiver.position.set(0, BODY_Y + 0.004, -0.03);
  group.add(receiver);

  const bolt = new THREE.Mesh(boxGeo(0.09, 0.02, 0.024), mats.receiver);
  // Straight-pull handle sticking out to the right, unmistakable at any angle.
  bolt.position.set(0.06, BODY_Y + 0.026, 0.03);
  group.add(bolt);

  const grip = new THREE.Mesh(boxGeo(0.042, 0.115, 0.055), mats.stock);
  grip.position.set(0, BODY_Y - 0.09, 0.075);
  grip.rotation.x = 0.22;
  group.add(grip);

  // --- Stock, barrel and magazine. `irons: false`: this rifle has none.
  // --------
  const fittedParts = mountSwaps(group, mats, DMR_SWAPS, mods, bare, a, false);

  // --- The scope: THE feature ----------------------------------------------
  //
  // `factory_scope_4x` is a real part, drawn here rather than via `OPTIC_IDS`
  // (which means "bolt-on lump" and hides irons). Four cases:
  //   factory_scope_4x fitted        → drawn
  //   a loose optic fitted           → not (applyMods draws that one)
  //   slot empty, "missing" view     → not (visibly stripped)
  // slot empty, "factory" view → drawn (a remote player's rifle ships scoped)
  let factoryScope: ScopeSurface | undefined;
  if (!hasOptic(mods) && (mods.includes("factory_scope_4x") || bare === "factory")) {
    factoryScope = dmrFactoryScope(group, mats);
  }

  const muzzle = addMuzzle(group, a.barrelY, a.muzzleZ);
  const fitted = applyMods(group, mats, mods, a, muzzle);
  const lengthM = 1.05 + (DMR_ANCHORS.muzzleZ - a.muzzleZ) + fitted.extraLength;
  return finish(group, mats, muzzle, lengthM, a, {
    grips: DMR_GRIPS,
    magMesh: fittedParts.magMesh,
    // A fitted optic wins (the factory glass was never built in that branch).
    // Either carries the picture-in-picture; `opticZoom` answers off the part.
    scope: fitted.scope ?? factoryScope,
  });
}

// ---------------------------------------------------------------------------

/**
 * Dienstpistole 9 — the sidearm: a short bar with a handle under the back of
 * it, a third of the carbine's length. Anchors: optic, muzzle, magazine
 * (floorplate at `magY`), lamp under the dust cover and the barrel; the rest
 * exist so leader lines have somewhere to land.
 */
const PISTOL_ANCHORS: AttachAnchors = {
  barrelY: -0.028,
  muzzleZ: -0.16,
  forendY: -0.063,
  forendZ: -0.1,
  magY: -0.15,
  magZ: 0.02,
  buttY: -0.026,
  buttZ: 0.04,
  opticZ: -0.015,
  receiverY: -0.006,
  receiverZ: -0.05,
  barrelZ: -0.145,
  handguardY: -0.046,
  handguardZ: -0.09,
  pistolgripY: -0.098,
  pistolgripZ: 0.012,
  // On the frame rail, under the dust cover and forward of the trigger guard.
  tacticalY: -0.062,
  tacticalZ: -0.09,
};

/**
 * Both hands on the same handle: the support fist wraps the strong one, just
 * below and a shade forward of it.
 */
const PISTOL_GRIPS: GripPoints = {
  triggerY: -0.072,
  triggerZ: 0.008,
  supportY: -0.105,
  supportZ: -0.012,
};

// --- The sidearm's two components --------------------------------------------
//
// The magazine lives inside the grip — only the floorplate shows — but is
// still a component so an empty well shows a bare grip frame. No `magMesh`.
// The barrel component is cut at the FRONT FACE OF THE FRAME: the slide stays
// (rear notch, serrations, optic mount) and the tube plus the 2 cm of slide
// overhanging the dust cover leave with the barrel, front sight and all.

/**
 * The slide rides barely under the sight axis — a handgun has no deep receiver.
 */
const PISTOL_BODY_Y = -0.026;

function pistolMagazine(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  // The floorplate — the only part of the magazine the assembled pistol shows.
  put(g, new THREE.Mesh(boxGeo(0.03, 0.012, 0.042), mats.receiver), 0, -0.155, 0.02);
  // The box itself, hidden by the gun but shown on the tile (`buildPart`):
  // 9.8 cm rising out of the plate at the grip's rake, sized to sit inside
  // the grip `buildPistol` draws with a millimetre or two of clearance on
  // every face so nothing z-fights.
  const body = put(
    g,
    new THREE.Mesh(boxGeo(0.022, 0.098, 0.024), mats.receiver),
    0,
    -0.1012,
    0.0097,
  );
  body.rotation.x = -0.26;
  return { group: g, magY: -0.161, magZ: 0.02 };
}

function pistolBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  const g = new THREE.Group();
  const y = PISTOL_BODY_Y;
  // The overhang: the last 2 cm of slide forward of the dust cover. With the
  // 16 cm that stays on the gun this is exactly the old 18 cm slide.
  put(g, new THREE.Mesh(boxGeo(0.03, 0.04, 0.02), mats.receiver), 0, y, -0.13);
  // Two centimetres of threaded tube out of the end of it, and that is the lot.
  put(g, new THREE.Mesh(boxGeo(0.016, 0.016, 0.035), mats.barrel), 0, y - 0.002, -0.145);
  // The front blade rides the slide's nose and leaves with it. Own size rather
  // than `frontSight` so the gun stays pixel-identical.
  if (ctx.irons) {
    put(g, new THREE.Mesh(boxGeo(0.004, 0.016, 0.006), mats.sight), 0, -0.008, -0.132);
  }
  return { group: g, muzzleZ: -0.16 };
}

const PISTOL_SWAPS: WeaponSwaps = {
  barrel: {
    factory: pistolBarrel,
    factoryKind: "detachable",
    // One part, no ladder: the slot exists so every gun has a vital barrel.
    parts: { pistol_barrel: pistolBarrel },
    // No barrel: the gun ends at the front face of the frame and the slide, and
    // the flash comes out of the hole they leave between them.
    emptyMuzzleZ: -0.125,
  },
  mag: {
    factory: pistolMagazine,
    factoryKind: "detachable",
    parts: { pistol_mag_15: pistolMagazine },
  },
};

function buildPistol(mods: readonly string[], bare: BareSlotView): BuiltWeapon {
  const group = new THREE.Group();
  group.name = "weapon:pistol";

  const mats = makeMaterials();
  const a: AttachAnchors = { ...PISTOL_ANCHORS };
  const irons = !hasOptic(mods);
  const SIGHT_Y = 0;
  const BODY_Y = PISTOL_BODY_Y;

  // --- Slide: everything back of the dust cover's front face -----------------
  const slide = new THREE.Mesh(boxGeo(0.03, 0.04, 0.16), mats.receiver);
  slide.position.set(0, BODY_Y, -0.04);
  group.add(slide);

  // Cocking serrations at the rear, the one piece of detail worth the boxes.
  for (const z of [0.004, 0.012, 0.02]) {
    const rib = new THREE.Mesh(boxGeo(0.034, 0.034, 0.004), mats.barrel);
    rib.position.set(0, BODY_Y, z);
    group.add(rib);
  }

  // --- Frame: dust cover under the slide, grip raked back under the breech ---
  const frame = new THREE.Mesh(boxGeo(0.026, 0.018, 0.15), mats.receiver);
  frame.position.set(0, BODY_Y - 0.028, -0.045);
  group.add(frame);

  // Negative rotation on x swings the BOTTOM of the box toward +z, i.e. back
  // toward the shooter — which is exactly the rake of a handgun's backstrap.
  const grip = new THREE.Mesh(boxGeo(0.028, 0.105, 0.04), mats.barrel);
  grip.position.set(0, BODY_Y - 0.072, 0.012);
  grip.rotation.x = -0.26;
  group.add(grip);

  // --- Trigger guard: a front strut and a bow into the front strap -----------
  const guardFront = new THREE.Mesh(boxGeo(0.012, 0.032, 0.01), mats.receiver);
  guardFront.position.set(0, BODY_Y - 0.051, -0.043);
  group.add(guardFront);

  const guardBow = new THREE.Mesh(boxGeo(0.012, 0.008, 0.055), mats.receiver);
  guardBow.position.set(0, BODY_Y - 0.064, -0.018);
  group.add(guardBow);

  const trigger = new THREE.Mesh(boxGeo(0.008, 0.018, 0.008), mats.barrel);
  trigger.position.set(0, BODY_Y - 0.047, -0.018);
  group.add(trigger);

  // --- The barrel and the magazine, when there are any -----------------------
  mountSwaps(group, mats, PISTOL_SWAPS, mods, bare, a, irons);

  // --- Sights: rear notch only, on the part of the slide that stays ----------
  if (irons) {
    const rearL = new THREE.Mesh(boxGeo(0.006, 0.012, 0.01), mats.sight);
    rearL.position.set(-0.009, SIGHT_Y - 0.006, 0.032);
    group.add(rearL);
    const rearR = new THREE.Mesh(boxGeo(0.006, 0.012, 0.01), mats.sight);
    rearR.position.set(0.009, SIGHT_Y - 0.006, 0.032);
    group.add(rearR);
  }

  const muzzle = addMuzzle(group, a.barrelY, a.muzzleZ);
  const fitted = applyMods(group, mats, mods, a, muzzle);
  // The same arithmetic every other gun uses, and it stopped being a constant
  // the day the tube could come off: a stripped sidearm is 3.5 cm shorter.
  const lengthM = 0.2 + (PISTOL_ANCHORS.muzzleZ - a.muzzleZ) + fitted.extraLength;
  // No `magMesh`: a handgun's box lives inside the grip and only its floorplate
  // is ever visible, so there is no mesh to drop that would not be the grip.
  return finish(group, mats, muzzle, lengthM, a, {
    grips: PISTOL_GRIPS,
    scope: fitted.scope,
  });
}

// ---------------------------------------------------------------------------

/**
 * Eastern carbine — the surplus self-loader. Three features no other gun has:
 * the CURVED magazine (two boxes at increasing rake), the GAS TUBE above the
 * barrel, and wooden furniture in three separate pieces around a stamped box.
 * It has no base silhouette — six components, and what is fitted decides which
 * exist. Anchors: the only gun that fills all ten.
 */
const EAST_CARBINE_ANCHORS: AttachAnchors = {
  barrelY: -0.035,
  muzzleZ: -0.44,
  forendY: -0.085,
  forendZ: -0.2,
  magY: -0.2,
  magZ: -0.055,
  buttY: -0.073,
  buttZ: 0.315,
  opticZ: -0.02,
  receiverY: -0.008,
  receiverZ: 0.02,
  barrelZ: -0.28,
  handguardY: -0.06,
  handguardZ: -0.2,
  pistolgripY: -0.13,
  pistolgripZ: 0.075,
  // Forward of the wooden guard and below it, where a clamp on the underside
  // rail would actually sit — clear of the support hand's fist at `forendZ`.
  tacticalY: -0.09,
  tacticalZ: -0.245,
};

/** Trigger hand on the raked bakelite grip; support hand on the wooden guard,
 *  which is the only part of the fore-end that is not a hot gas tube. */
const EAST_CARBINE_GRIPS: GripPoints = {
  triggerY: -0.135,
  triggerZ: 0.072,
  supportY: -0.075,
  supportZ: -0.2,
};

/** The rifle's body height, and the barrel axis above it. */
const OST_BODY_Y = -0.055;
const OST_BARREL_Y = OST_BODY_Y + 0.02;

// --- The Eastern carbine's components -------------------------------------------
//
// Six detachable swap slots — the gun the component split exists for. THE GAS
// TUBE GOES WITH THE BARREL: the gas block is bolted to it and the tube runs
// back to the trunnion, so taking the barrel off leaves a hole rather than a
// floating tube.

function ostWoodStock(mats: WeaponMaterials): WeaponComponent {
  // The factory club. The wrist is part of the stock, not the receiver —
  // the underfolder has none.
  const g = new THREE.Group();
  const y = OST_BODY_Y;
  put(g, new THREE.Mesh(boxGeo(0.044, 0.058, 0.1), mats.stock), 0, y - 0.004, 0.14);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.095, 0.16), mats.stock), 0, y - 0.018, 0.235);
  put(g, new THREE.Mesh(boxGeo(0.056, 0.1, 0.012), mats.receiver), 0, y - 0.018, 0.315);
  return { group: g, buttY: y - 0.018, buttZ: 0.321 };
}

function ostFoldingStock(mats: WeaponMaterials): WeaponComponent {
  // The underfolder: two struts, a shoulder plate and the hinge block under
  // the rear trunnion. Struts run just under the receiver floor and the hinge
  // spans into the receiver, so the stock is latched rather than floating.
  const g = new THREE.Group();
  const y = OST_BODY_Y - 0.052;
  for (const x of [-0.022, 0.022]) {
    put(g, new THREE.Mesh(boxGeo(0.008, 0.008, 0.17), mats.receiver), x, y, 0.235);
  }
  put(g, new THREE.Mesh(boxGeo(0.055, 0.03, 0.012), mats.receiver), 0, y, 0.315);
  put(g, new THREE.Mesh(boxGeo(0.052, 0.036, 0.036), mats.receiver), 0, y + 0.006, 0.142);
  return { group: g, buttY: y, buttZ: 0.321 };
}

function ostStampedCover(mats: WeaponMaterials): WeaponComponent {
  // A folded sheet. Its top stops just below the sight axis.
  const g = new THREE.Group();
  put(g, new THREE.Mesh(boxGeo(0.046, 0.016, 0.2), mats.receiver), 0, -0.01, 0.02);
  put(g, new THREE.Mesh(boxGeo(0.03, 0.02, 0.016), mats.receiver), 0, -0.012, 0.125);
  return { group: g };
}

function ostRailedCover(mats: WeaponMaterials): WeaponComponent {
  // A milled slab with a rail down its spine. An optic on it is drawn by its
  // own case at `opticZ`, directly over this block.
  const g = new THREE.Group();
  put(g, new THREE.Mesh(boxGeo(0.046, 0.014, 0.2), mats.receiver), 0, -0.012, 0.02);
  put(g, new THREE.Mesh(boxGeo(0.022, 0.008, 0.18), mats.sight), 0, -0.0055, 0.02);
  // Four cross-notches: the cheapest thing that reads as "you can clamp
  // something here". Kept under y = 0 like everything else on the spine.
  for (const dz of [-0.06, -0.02, 0.02, 0.06]) {
    put(g, new THREE.Mesh(boxGeo(0.026, 0.004, 0.006), mats.sight), 0, -0.004, 0.02 + dz);
  }
  put(g, new THREE.Mesh(boxGeo(0.03, 0.02, 0.016), mats.receiver), 0, -0.014, 0.125);
  return { group: g };
}

function ostBakeliteGrip(mats: WeaponMaterials): WeaponComponent {
  const g = new THREE.Group();
  const grip = put(g, new THREE.Mesh(boxGeo(0.04, 0.105, 0.05), mats.stock), 0, -0.13, 0.075);
  // Negative: the bottom swings back toward the shooter, which is the rake of
  // the moulded grip.
  grip.rotation.x = -0.18;
  put(g, new THREE.Mesh(boxGeo(0.042, 0.012, 0.048), mats.stock), 0, -0.181, 0.084);
  return { group: g };
}

function ostSportGrip(mats: WeaponMaterials): WeaponComponent {
  // Fatter, rubber-backed, and with a shelf under the web of the thumb — the
  // one bulge that tells the two grips apart from across a room.
  const g = new THREE.Group();
  const grip = put(g, new THREE.Mesh(boxGeo(0.048, 0.1, 0.056), mats.receiver), 0, -0.128, 0.075);
  grip.rotation.x = -0.18;
  put(g, new THREE.Mesh(boxGeo(0.05, 0.016, 0.026), mats.stock), 0, -0.078, 0.103);
  return { group: g };
}

function ostWoodHandguard(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // Laminated birch in two halves around the gas tube, with the clamp rings
  // that hold them on. The lower half is the piece the support hand holds.
  const g = new THREE.Group();
  const span = spanToMuzzle(-0.115, -0.27, ctx.muzzleZ, 0.03, 0.06);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.045, span.depth), mats.stock), 0, -0.06, span.center);
  put(g, new THREE.Mesh(boxGeo(0.046, 0.026, span.depth - 0.02), mats.stock), 0, -0.024, span.center);
  for (const dz of [-0.05, 0.05]) {
    put(g, new THREE.Mesh(boxGeo(0.056, 0.05, 0.01), mats.stock), 0, -0.06, span.center + dz);
  }
  return { group: g };
}

function ostRailedHandguard(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // An alloy shell with rails on three faces — the rails are what the part is.
  const g = new THREE.Group();
  const span = spanToMuzzle(-0.115, -0.275, ctx.muzzleZ, 0.03, 0.06);
  const railLen = span.depth - 0.01;
  put(g, new THREE.Mesh(boxGeo(0.05, 0.05, span.depth), mats.receiver), 0, -0.06, span.center);
  put(g, new THREE.Mesh(boxGeo(0.02, 0.008, railLen), mats.sight), 0, -0.088, span.center);
  for (const x of [-0.028, 0.028]) {
    put(g, new THREE.Mesh(boxGeo(0.008, 0.02, railLen), mats.sight), x, -0.06, span.center);
  }
  return { group: g };
}

function ostBarrel(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // Barrel, gas block, gas tube, front sight block and muzzle thread — the
  // whole front end as one assembly.
  const g = new THREE.Group();
  // The breech end reaches INTO the receiver (front face z = −0.12): the tube
  // used to stop at −0.13 and leave a 1 cm slit under the gas tube.
  put(g, new THREE.Mesh(boxGeo(0.024, 0.024, 0.33), mats.barrel), 0, OST_BARREL_Y, -0.265);
  put(g, new THREE.Mesh(boxGeo(0.022, 0.022, 0.17), mats.barrel), 0, OST_BODY_Y + 0.043, -0.2);
  put(g, new THREE.Mesh(boxGeo(0.03, 0.05, 0.032), mats.barrel), 0, OST_BODY_Y + 0.026, -0.288);
  put(g, new THREE.Mesh(boxGeo(0.03, 0.042, 0.03), mats.barrel), 0, OST_BARREL_Y + 0.008, -0.405);
  // One collar, so the thread is visibly there before anything is screwed to it.
  put(g, new THREE.Mesh(boxGeo(0.03, 0.03, 0.016), mats.barrel), 0, OST_BARREL_Y, -0.43);
  // The post sits inside the sight block's ears; the ears themselves are
  // deliberately not drawn, exactly as the carbine's hood is not.
  if (ctx.irons) frontSight(g, mats, 0.02, -0.405);
  return { group: g, muzzleZ: -0.44 };
}

function ostBanana(mats: WeaponMaterials): WeaponComponent {
  // The curved magazine: two boxes at increasing rake (positive x-rotation
  // swings the bottom toward −z). The lower half is a CHILD of the upper so
  // the reload drops the whole banana.
  const g = new THREE.Group();
  const upper = put(g, new THREE.Mesh(boxGeo(0.032, 0.075, 0.05), mats.receiver), 0, -0.105, -0.03);
  upper.rotation.x = 0.18;

  const lower = new THREE.Mesh(boxGeo(0.032, 0.07, 0.05), mats.receiver);
  // In the upper box's frame: world offset (0, −0.062, −0.025) rotated back
  // through its rake; 0.28 rad is the difference between the two rakes.
  lower.position.set(0, -0.0655, -0.0135);
  lower.rotation.x = 0.28;
  upper.add(lower);

  const floor = new THREE.Mesh(boxGeo(0.036, 0.012, 0.052), mats.receiver);
  floor.position.set(0, -0.041, 0);
  lower.add(floor);

  return { group: g, magY: -0.207, magZ: -0.055, magMesh: g };
}

function ostShortBanana(mats: WeaponMaterials): WeaponComponent {
  // The ten: a genuinely short banana, level with the trigger guard.
  const g = new THREE.Group();
  const box = put(g, new THREE.Mesh(boxGeo(0.032, 0.062, 0.05), mats.receiver), 0, -0.098, -0.032);
  box.rotation.x = 0.18;
  const floor = new THREE.Mesh(boxGeo(0.036, 0.012, 0.052), mats.receiver);
  floor.position.set(0, -0.037, 0);
  box.add(floor);
  return { group: g, magY: -0.14, magZ: -0.05, magMesh: g };
}

const EAST_CARBINE_SWAPS: WeaponSwaps = {
  receiver: {
    factory: ostStampedCover,
    factoryKind: "detachable",
    parts: { stamped_top_cover: ostStampedCover, railed_top_cover: ostRailedCover },
  },
  barrel: {
    factory: ostBarrel,
    factoryKind: "detachable",
    parts: { ec_barrel: ostBarrel },
    // No barrel: the flash comes out of the front trunnion, which is where the
    // hole in the rifle actually is.
    emptyMuzzleZ: -0.125,
  },
  pistolgrip: {
    factory: ostBakeliteGrip,
    factoryKind: "detachable",
    parts: { bakelite_grip: ostBakeliteGrip, ergo_grip: ostSportGrip },
  },
  handguard: {
    factory: ostWoodHandguard,
    factoryKind: "detachable",
    parts: { wood_handguard: ostWoodHandguard, railed_handguard: ostRailedHandguard },
  },
  stock: {
    factory: ostWoodStock,
    factoryKind: "detachable",
    parts: { wood_stock: ostWoodStock, folding_stock: ostFoldingStock },
  },
  mag: {
    factory: ostBanana,
    factoryKind: "detachable",
    parts: { ec_mag_30: ostBanana, ec_mag_10: ostShortBanana },
  },
};

function buildEastCarbine(mods: readonly string[], bare: BareSlotView): BuiltWeapon {
  const group = new THREE.Group();
  group.name = "weapon:east_carbine";

  const mats = makeMaterials();
  const SIGHT_Y = 0;
  const BODY_Y = OST_BODY_Y;
  const irons = !hasOptic(mods);
  const a: AttachAnchors = { ...EAST_CARBINE_ANCHORS };

  // --- Stamped receiver: the one thing no slot can take away
  // ------------------
  const receiver = new THREE.Mesh(boxGeo(0.05, 0.082, 0.28), mats.receiver);
  receiver.position.set(0, BODY_Y, 0.02);
  group.add(receiver);

  // Right-hand charging handle, sticking out where everybody expects it.
  const charging = new THREE.Mesh(boxGeo(0.032, 0.016, 0.04), mats.receiver);
  charging.position.set(0.038, BODY_Y + 0.03, -0.03);
  group.add(charging);

  const trigger = new THREE.Mesh(boxGeo(0.012, 0.028, 0.012), mats.receiver);
  trigger.position.set(0, BODY_Y - 0.048, 0.04);
  group.add(trigger);

  const guard = new THREE.Mesh(boxGeo(0.03, 0.008, 0.055), mats.receiver);
  guard.position.set(0, BODY_Y - 0.062, 0.035);
  group.add(guard);

  // --- All six assemblies ------------------------------------------------------
  const fittedParts = mountSwaps(group, mats, EAST_CARBINE_SWAPS, mods, bare, a, irons);

  // --- Iron sights: rear notch only; the front post is on the barrel. Skipped
  // once an optic is fitted. -----------------------------------------------
  if (irons) {
    const rearBase = new THREE.Mesh(boxGeo(0.034, 0.014, 0.026), mats.sight);
    rearBase.position.set(0, SIGHT_Y - 0.023, -0.135);
    group.add(rearBase);

    for (const x of [-0.011, 0.011]) {
      const post = new THREE.Mesh(boxGeo(0.008, 0.018, 0.02), mats.sight);
      post.position.set(x, SIGHT_Y - 0.009, -0.135);
      group.add(post);
    }
  }

  const muzzle = addMuzzle(group, a.barrelY, a.muzzleZ);
  const fitted = applyMods(group, mats, mods, a, muzzle);
  const lengthM = 0.88 + (EAST_CARBINE_ANCHORS.muzzleZ - a.muzzleZ) + fitted.extraLength;
  return finish(group, mats, muzzle, lengthM, a, {
    grips: EAST_CARBINE_GRIPS,
    // Whichever box is in it, and it drops whole: the banana's lower section is
    // parented to its upper one inside the component.
    magMesh: fittedParts.magMesh,
    scope: fitted.scope,
  });
}

// ---------------------------------------------------------------------------

/**
 * Sniper rifle — the longest gun in the game: 1.2 m, fat matte scope on tall
 * rings, a straight alloy beam for a stock. Like the Eastern carbine it has no
 * base silhouette beyond the action and bolt; unlike it there are no irons at
 * any barrel length. Anchors: fills all ten kinds (no tube).
 */
const SNIPER_ANCHORS: AttachAnchors = {
  barrelY: -0.063,
  muzzleZ: -0.76,
  forendY: -0.095,
  forendZ: -0.3,
  magY: -0.187,
  magZ: -0.03,
  buttY: -0.097,
  buttZ: 0.405,
  opticZ: -0.06,
  receiverY: -0.041,
  receiverZ: -0.03,
  barrelZ: -0.47,
  handguardY: -0.09,
  handguardZ: -0.3,
  pistolgripY: -0.175,
  pistolgripZ: 0.078,
  // Under the handguard and forward of the support hand, where a clamp on the
  // bottom rail actually sits. `mountSwaps` pulls it back with a short barrel.
  tacticalY: -0.1,
  tacticalZ: -0.38,
};

/**
 * Scope over bore, as on the hunting rifle; the support hand is the furthest
 * forward in the roster.
 */
const SNIPER_GRIPS: GripPoints = {
  triggerY: -0.148,
  triggerZ: 0.068,
  supportY: -0.098,
  supportZ: -0.29,
};

/**
 * Deliberately the hunting rifle's number: `dmrFlushMagazine`, `dmrDeepMagazine`
 * and `dmrSkeletonStock` are authored against it and this gun shares all three.
 */
const SNIPER_BODY_Y = DMR_BODY_Y;
/** The bore, above the body and well below the sight axis: this is what makes
 *  the scope sit high enough to need a cheek riser under it. */
const SNIPER_BARREL_Y = SNIPER_BODY_Y + 0.022;

// --- The sniper rifle's components -----------------------------------------
//
// Five detachable swap slots plus the well; the barrel hosts the muzzle thread
// and the railed handguard hosts the rail furniture. Both barrels end in a
// visible collar so a bare thread reads as one.

function sniperBarrel(mats: WeaponMaterials): WeaponComponent {
  // Heavy and fluted: a tall narrow box through a square one gives a
  // plus-shaped section.
  const g = new THREE.Group();
  const y = SNIPER_BARREL_Y;
  put(g, new THREE.Mesh(boxGeo(0.036, 0.036, 0.58), mats.barrel), 0, y, -0.47);
  put(g, new THREE.Mesh(boxGeo(0.026, 0.046, 0.54), mats.barrel), 0, y, -0.47);
  put(g, new THREE.Mesh(boxGeo(0.04, 0.04, 0.018), mats.barrel), 0, y, -0.751);
  return { group: g, muzzleZ: -0.76 };
}

function sniperShortBarrel(mats: WeaponMaterials): WeaponComponent {
  // Twenty centimetres off the front, and that is the entire read: same section,
  // same flutes, same thread, and a gun that ends where the handguard does.
  const g = new THREE.Group();
  const y = SNIPER_BARREL_Y;
  put(g, new THREE.Mesh(boxGeo(0.036, 0.036, 0.38), mats.barrel), 0, y, -0.37);
  put(g, new THREE.Mesh(boxGeo(0.026, 0.046, 0.34), mats.barrel), 0, y, -0.37);
  put(g, new THREE.Mesh(boxGeo(0.044, 0.044, 0.018), mats.barrel), 0, y, -0.551);
  return { group: g, muzzleZ: -0.56 };
}

function sniperChassis(mats: WeaponMaterials): WeaponComponent {
  // Alloy beam, squared butt and cheek riser — the riser puts the eye under
  // glass that sits 3 cm above the bore.
  const g = new THREE.Group();
  const y = SNIPER_BODY_Y;
  put(g, new THREE.Mesh(boxGeo(0.046, 0.052, 0.3), mats.receiver), 0, y - 0.012, 0.26);
  put(g, new THREE.Mesh(boxGeo(0.06, 0.13, 0.018), mats.receiver), 0, y - 0.012, 0.396);
  put(g, new THREE.Mesh(boxGeo(0.05, 0.034, 0.18), mats.stock), 0, y + 0.032, 0.225);
  return { group: g, buttY: y - 0.012, buttZ: 0.405 };
}

function sniperFoldingChassis(mats: WeaponMaterials): WeaponComponent {
  // The hinge block is the identity, with a visibly shorter beam behind it.
  const g = new THREE.Group();
  const y = SNIPER_BODY_Y;
  put(g, new THREE.Mesh(boxGeo(0.05, 0.058, 0.055), mats.receiver), 0, y - 0.008, 0.15);
  put(g, new THREE.Mesh(boxGeo(0.038, 0.044, 0.2), mats.receiver), 0, y - 0.014, 0.275);
  put(g, new THREE.Mesh(boxGeo(0.056, 0.115, 0.016), mats.receiver), 0, y - 0.014, 0.383);
  put(g, new THREE.Mesh(boxGeo(0.046, 0.03, 0.14), mats.stock), 0, y + 0.03, 0.245);
  return { group: g, buttY: y - 0.014, buttZ: 0.391 };
}

function sniperGrip(mats: WeaponMaterials): WeaponComponent {
  // Barely raked, which is the part: a rifle fired one deliberate shot at a time
  // wants the wrist straight behind the trigger, not curled under it.
  const g = new THREE.Group();
  const grip = put(g, new THREE.Mesh(boxGeo(0.042, 0.115, 0.05), mats.stock), 0, SNIPER_BODY_Y - 0.09, 0.078);
  grip.rotation.x = -0.05;
  put(g, new THREE.Mesh(boxGeo(0.046, 0.012, 0.05), mats.stock), 0, SNIPER_BODY_Y - 0.151, 0.081);
  return { group: g };
}

function sniperSportGrip(mats: WeaponMaterials): WeaponComponent {
  // The Eastern carbine's `ergo_grip`, drawn against this gun's body.
  const g = new THREE.Group();
  const grip = put(g, new THREE.Mesh(boxGeo(0.048, 0.106, 0.056), mats.receiver), 0, SNIPER_BODY_Y - 0.086, 0.078);
  grip.rotation.x = -0.05;
  put(g, new THREE.Mesh(boxGeo(0.05, 0.016, 0.026), mats.stock), 0, SNIPER_BODY_Y - 0.03, 0.106);
  return { group: g };
}

function sniperTubeHandguard(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // A smooth alloy tube with two clamp rings and nothing on it.
  const g = new THREE.Group();
  const y = SNIPER_BARREL_Y;
  const span = spanToMuzzle(-0.18, -0.44, ctx.muzzleZ, 0.045, 0.08);
  put(g, new THREE.Mesh(boxGeo(0.056, 0.056, span.depth), mats.receiver), 0, y, span.center);
  for (const dz of [-0.07, 0.07]) {
    put(g, new THREE.Mesh(boxGeo(0.062, 0.062, 0.012), mats.receiver), 0, y, span.center + dz);
  }
  return { group: g };
}

function sniperRailHandguard(mats: WeaponMaterials, ctx: ComponentCtx): WeaponComponent {
  // The same tube with rails along top and bottom (two, not three — side
  // rails would be invisible detail). The top strip stops well under y = 0.
  const g = new THREE.Group();
  const y = SNIPER_BARREL_Y;
  const span = spanToMuzzle(-0.18, -0.45, ctx.muzzleZ, 0.045, 0.08);
  const railLen = span.depth - 0.014;
  put(g, new THREE.Mesh(boxGeo(0.054, 0.054, span.depth), mats.receiver), 0, y, span.center);
  put(g, new THREE.Mesh(boxGeo(0.024, 0.008, railLen), mats.sight), 0, y + 0.031, span.center);
  put(g, new THREE.Mesh(boxGeo(0.024, 0.008, railLen), mats.sight), 0, y - 0.031, span.center);
  // Cross-notches on the top strip: the cheapest thing that reads as "you can
  // clamp something here", and the same four the railed top cover wears.
  for (const dz of [-0.06, -0.02, 0.02, 0.06]) {
    put(g, new THREE.Mesh(boxGeo(0.028, 0.005, 0.006), mats.sight), 0, y + 0.036, span.center + dz);
  }
  return { group: g };
}

const SNIPER_SWAPS: WeaponSwaps = {
  barrel: {
    factory: sniperBarrel,
    factoryKind: "detachable",
    parts: { sniper_barrel: sniperBarrel, sniper_barrel_short: sniperShortBarrel },
    // The front face of the action, where both tubes thread in. With no barrel
    // at all the flash comes out of the hole, which is where it is.
    emptyMuzzleZ: -0.18,
  },
  stock: {
    factory: sniperChassis,
    factoryKind: "detachable",
    parts: {
      sniper_chassis: sniperChassis,
      sniper_chassis_folding: sniperFoldingChassis,
      // The hunting rifle's target stock, reused — same part, same body height.
      thumbhole_stock: dmrSkeletonStock,
    },
  },
  pistolgrip: {
    factory: sniperGrip,
    factoryKind: "detachable",
    parts: { sniper_grip: sniperGrip, ergo_grip: sniperSportGrip },
  },
  handguard: {
    factory: sniperTubeHandguard,
    factoryKind: "detachable",
    parts: {
      sniper_handguard: sniperTubeHandguard,
      sniper_rail_handguard: sniperRailHandguard,
    },
  },
  mag: {
    // The hunting rifle's two boxes — the same parts in the item DB.
    factory: dmrFlushMagazine,
    factoryKind: "detachable",
    parts: { rifle_mag_5: dmrFlushMagazine, rifle_mag_10: dmrDeepMagazine },
  },
};

/**
 * The sniper rifle's own 10× glass, pulled out for `buildPart` like
 * `dmrFactoryScope`.
 */
function sniperFactoryScope(group: THREE.Group, mats: WeaponMaterials): ScopeSurface {
  // The sight axis. A local const rather than a parameter: it is 0 by the
  // file's local-space contract, and naming it is what makes the block read.
  const SIGHT_Y = 0;

  const tube = new THREE.Mesh(boxGeo(0.05, 0.05, 0.34), mats.sight);
  tube.position.set(0, SIGHT_Y, -0.075);
  group.add(tube);

  const bell = new THREE.Mesh(boxGeo(0.068, 0.068, 0.085), mats.sight);
  bell.position.set(0, SIGHT_Y, -0.288);
  group.add(bell);

  const objective = new THREE.Mesh(boxGeo(0.05, 0.05, 0.006), mats.bead);
  objective.position.set(0, SIGHT_Y, -0.334);
  group.add(objective);

  // The ocular: the same sandwich `addOcular` builds. No flare — the tube
  // (5 cm) encloses the disc (4.8).
  const glass = lensDisc(0.024);
  mats.all.push(glass.mat);
  glass.mesh.position.set(0, SIGHT_Y, 0.0955);
  group.add(glass.mesh);
  const surface: ScopeSurface = { lens: glass.mesh, radiusM: 0.024 };

  const scopeMark = reticleQuad();
  mats.all.push(scopeMark.mat);
  scopeMark.mesh.position.set(0, SIGHT_Y, 0.096);
  group.add(scopeMark.mesh);

  // TALL rings, because the bell is 6.8 cm across and would otherwise be
  // buried in the action. They are also what the cheek riser exists to answer.
  for (const z of [-0.19, 0.03]) {
    const ring = new THREE.Mesh(boxGeo(0.032, 0.056, 0.024), mats.receiver);
    ring.position.set(0, SIGHT_Y - 0.037, z);
    group.add(ring);
  }
  return surface;
}

function buildSniper(mods: readonly string[], bare: BareSlotView): BuiltWeapon {
  const group = new THREE.Group();
  group.name = "weapon:sniper";

  const mats = makeMaterials();
  const BODY_Y = SNIPER_BODY_Y;
  const a: AttachAnchors = { ...SNIPER_ANCHORS };

  // --- The action: the one thing no slot can take away
  // ------------------------
  const receiver = new THREE.Mesh(boxGeo(0.052, 0.086, 0.3), mats.receiver);
  receiver.position.set(0, BODY_Y + 0.001, -0.03);
  group.add(receiver);

  // The straight bolt handle, sticking out to the right and longer than the
  // hunting rifle's: at 30 rpm the throw is a thing the player watches happen.
  const bolt = new THREE.Mesh(boxGeo(0.1, 0.022, 0.026), mats.receiver);
  bolt.position.set(0.066, BODY_Y + 0.03, 0.055);
  group.add(bolt);

  const trigger = new THREE.Mesh(boxGeo(0.012, 0.03, 0.014), mats.receiver);
  trigger.position.set(0, BODY_Y - 0.05, 0.045);
  group.add(trigger);

  const guard = new THREE.Mesh(boxGeo(0.032, 0.008, 0.058), mats.receiver);
  guard.position.set(0, BODY_Y - 0.066, 0.04);
  group.add(guard);

  // --- All five assemblies, plus the box. `irons: false`: no iron sights at
  // any
  // length; stripped of its optic in "missing" view it has no sight at all. ---
  const fittedParts = mountSwaps(group, mats, SNIPER_SWAPS, mods, bare, a, false);

  // --- The glass: the same four cases as `buildDmr`. `sniper_scope_10x` is
  // drawn here, so it is absent from `OPTIC_IDS`. Longer and fatter than the
  // 4× in every dimension — magnification is the read at this range. -------
  let factoryScope: ScopeSurface | undefined;
  if (!hasOptic(mods) && (mods.includes("sniper_scope_10x") || bare === "factory")) {
    factoryScope = sniperFactoryScope(group, mats);
  }

  const muzzle = addMuzzle(group, a.barrelY, a.muzzleZ);
  const fitted = applyMods(group, mats, mods, a, muzzle);
  const lengthM = 1.2 + (SNIPER_ANCHORS.muzzleZ - a.muzzleZ) + fitted.extraLength;
  return finish(group, mats, muzzle, lengthM, a, {
    grips: SNIPER_GRIPS,
    magMesh: fittedParts.magMesh,
    // A fitted optic wins; the factory glass was never built in that branch.
    scope: fitted.scope ?? factoryScope,
  });
}

// ---------------------------------------------------------------------------
// One part, on its own
// ---------------------------------------------------------------------------

/**
 * Every gun that can host a loose part, in roster order. Must follow every
 * `*_SWAPS` table (TDZ).
 */
const PART_HOSTS: readonly { swaps: WeaponSwaps; anchors: AttachAnchors }[] = [
  { swaps: CARBINE_SWAPS, anchors: CARBINE_ANCHORS },
  { swaps: SMG_SWAPS, anchors: SMG_ANCHORS },
  { swaps: SHOTGUN_SWAPS, anchors: SHOTGUN_ANCHORS },
  { swaps: DMR_SWAPS, anchors: DMR_ANCHORS },
  { swaps: PISTOL_SWAPS, anchors: PISTOL_ANCHORS },
  { swaps: EAST_CARBINE_SWAPS, anchors: EAST_CARBINE_ANCHORS },
  { swaps: SNIPER_SWAPS, anchors: SNIPER_ANCHORS },
];

/** A loose part's model. The same bargain as `BuiltWeapon`: the shared geometry
 *  stays, the materials and any lens disc belong to this build. */
export interface BuiltPart {
  group: THREE.Group;
  dispose(): void;
}

/**
 * The loose part `id` on its own — the picture on its inventory tile, rendered
 * by the same component builder the gun runs, so what is on the tile is what
 * bolts on. Drawn in its host's local space; `itemPortrait.ts` frames by
 * bounding box, so position is thrown away. The FIRST host in roster order
 * wins for parts several guns accept. `null` for an id this file cannot draw
 * (the caller must not retry — see `undrawable`).
 */
export function buildPart(id: string): BuiltPart | null {
  const mats = makeMaterials();

  // The two factory optics, which no `parts` table names and `applyMods` has
  // never drawn — each is its own rifle's scope block. See `OPTIC_IDS`.
  if (id === "factory_scope_4x" || id === "sniper_scope_10x") {
    const group = new THREE.Group();
    group.name = `part:${id}`;
    const scope =
      id === "factory_scope_4x" ? dmrFactoryScope(group, mats) : sniperFactoryScope(group, mats);
    return finishPart(group, mats, scope);
  }

  // A swap kind: the region this part would become. `irons: true` so a barrel
  // brings its front sight; `muzzleZ` is the host's.
  for (const host of PART_HOSTS) {
    for (const kind of SWAP_ORDER) {
      const make = host.swaps[kind]?.parts?.[id];
      if (make === undefined) continue;
      const built = make(mats, { irons: true, muzzleZ: host.anchors.muzzleZ });
      built.group.name = `part:${id}`;
      return finishPart(built.group, mats, undefined);
    }
  }

  // An add kind: a lump `applyMods` bolts on, at the carbine's anchors (the
  // one gun that fills every entry). The muzzle object is NOT added to the
  // group or it would drag the bounding box out to a barrel tip that is not
  // there.
  const group = new THREE.Group();
  group.name = `part:${id}`;
  const anchors: AttachAnchors = { ...CARBINE_ANCHORS };
  const fitted = applyMods(group, mats, [id], anchors, new THREE.Object3D());
  if (group.children.length === 0) {
    for (const m of mats.all) m.dispose();
    return null;
  }
  return finishPart(group, mats, fitted.scope);
}

/** `finish`'s half of the bargain, minus everything a loose part cannot answer
 *  — it has no muzzle, no anchors, no grips and no length. */
function finishPart(
  group: THREE.Group,
  mats: WeaponMaterials,
  scope: ScopeSurface | undefined,
): BuiltPart {
  return {
    group,
    dispose(): void {
      for (const m of mats.all) m.dispose();
      // Sized to its own scope, so it was built here and nothing else wants it.
      scope?.lens.geometry.dispose();
      group.clear();
    },
  };
}
